import { snapToPixel, lerp, clampRange, clamp01 } from '../util/math.js';
import { pathDistance, pointDistance, spotReached, movePointTowards } from '../util/interpolation.js';
import { orientX, screenXForYard, ballBreaksPlane } from '../field/metrics.js';
import {
  fumbleRecoveryHeadline,
  isDefensiveFumbleRecovery,
  stopReasonForPlay,
  playHash,
  isFumbleTurnover,
  turnoverReturnYards,
  turnoverSpotYardLine,
} from './helpers.js';
import { clonePlayers, findPlayer, carriedBallPoint, nearestPlayerToPoint, centerSnapBallPoint, blendBallPoint, setPlayerState } from '../player/model.js';
import { compactPathPoints, createMotionState, steerMotionState, playersFromMotionStates, freezeLiveFrame, cloneLiveFrame, spreadTargetAwayFromPeers, roleSpacingDistance, velocityMagnitude, guidePointAt, flowingGuideTarget, projectedPoint, curvedApproachTarget } from './track.js';
import { liveFrameCount } from './context.js';
import { playerPixelsPerFrame } from './speed.js';
import { lossRunLaneY, buildLossRunWaypoints, buildRunWaypoints } from './waypoints.js';
import { buildArcadeRunLiveSequence } from './live-run-arcade.js';
import type { FieldPlayer } from '../player/model.js';

function isOffensiveTrenchRole(role: string): boolean {
  return role === "LT" || role === "LG" || role === "C" || role === "RG" || role === "RT";
}

function resolvedRunFamily(play: any): string {
  if (play?.runFamily) return String(play.runFamily);
  const playId = String(play?.playId || "");
  if (playId === "inside-zone" || playId === "outside-zone" || playId === "split-zone") return "zone";
  if (playId === "power-o") return "power";
  if (playId === "counter-tray") return "counter";
  if (playId === "toss-sweep" || playId === "crack-toss") return "toss";
  if (playId === "zone-read" || playId === "speed-option" || playId === "boot-keeper") return "option";
  if (playId === "qb-draw") return "draw";
  if (playId === "qb-sneak") return "sneak";
  return "";
}

function runFrontFitPoint(
  job: string,
  role: string,
  target: { x: number; y: number },
  trenchHoldPoint: { x: number; y: number },
  carrierMotion: { x: number; y: number },
  context: { losX: number; centerY: number; direction: number },
  flowKey: string,
  openFieldBlend: number,
): { x: number; y: number } {
  const outsideSign = flowKey === "top" ? -1 : flowKey === "bottom" ? 1 : target.y < context.centerY ? -1 : target.y > context.centerY ? 1 : 0;
  const flowTowardCarrier = (amount: number) => snapToPixel(lerp(target.y, carrierMotion.y, amount));
  switch (job) {
    case "force":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 12 : 8, context.direction)),
        y: snapToPixel(lerp(target.y, carrierMotion.y + outsideSign * 18, 0.34 + openFieldBlend * 0.08)),
      };
    case "spill":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 10 : 7, context.direction)),
        y: flowTowardCarrier(0.2 + openFieldBlend * 0.06),
      };
    case "box":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 10 : 6, context.direction)),
        y: snapToPixel(lerp(target.y, carrierMotion.y + outsideSign * 8, 0.16 + openFieldBlend * 0.04)),
      };
    case "dent":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 8 : 6, context.direction)),
        y: flowTowardCarrier(0.18),
      };
    case "cutback":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 5 : 3, context.direction)),
        y: snapToPixel(lerp(target.y, carrierMotion.y - outsideSign * 14, 0.12 + openFieldBlend * 0.04)),
      };
    case "retrace":
      return {
        x: snapToPixel(orientX(context.losX, role.startsWith("DE") ? 7 : 4, context.direction)),
        y: flowTowardCarrier(0.16 + openFieldBlend * 0.04),
      };
    case "trail":
    default:
      return {
        x: trenchHoldPoint.x,
        y: snapToPixel(lerp(trenchHoldPoint.y, carrierMotion.y, role.startsWith("DE") ? 0.05 : 0.03)),
      };
  }
}

function forwardOnlyTargetX(currentX: number, targetX: number, direction: number): number {
  return direction > 0 ? Math.max(currentX, targetX) : Math.min(currentX, targetX);
}

function frontBattlePartnerRole(role: string): string | null {
  switch (role) {
    case "DE1":
      return "LT";
    case "DT1":
      return "LG";
    case "DT2":
      return "RG";
    case "DE2":
      return "RT";
    default:
      return null;
  }
}

function offensiveBattlePartnerRole(role: string): string | null {
  switch (role) {
    case "LT":
      return "DE1";
    case "LG":
      return "DT1";
    case "C":
      return "DT1";
    case "RG":
      return "DT2";
    case "RT":
      return "DE2";
    default:
      return null;
  }
}

function trenchRoleIndex(role: string): number {
  switch (role) {
    case "LT":
    case "DE1":
      return -2;
    case "LG":
    case "DT1":
      return -1;
    case "C":
      return 0;
    case "RG":
    case "DT2":
      return 1;
    case "RT":
    case "DE2":
      return 2;
    default:
      return 0;
  }
}

export function runTrenchEngagementTarget(
  blocker: { x: number; y: number },
  defender: { x: number; y: number },
  role: string,
  direction: number,
  flowKey: string,
  carrierY: number,
  openFieldBlend: number = 0,
): { x: number; y: number } {
  const roleIndex = trenchRoleIndex(role);
  const playsideSign = flowKey === "top" ? -1 : flowKey === "bottom" ? 1 : roleIndex < 0 ? -1 : roleIndex > 0 ? 1 : 0;
  const edgeRole = role === "LT" || role === "RT";
  const interiorRole = role === "C";
  const shoulderBias = edgeRole ? 8 : interiorRole ? 2 : 5;
  const laneBias = Math.sign(carrierY - defender.y) * (1 + openFieldBlend * 2);
  const targetX = forwardOnlyTargetX(
    blocker.x,
    snapToPixel(defender.x - direction * (edgeRole ? 10 : interiorRole ? 7 : 8)),
    direction,
  );
  const targetY = snapToPixel(
    lerp(blocker.y, defender.y, 0.62)
      + playsideSign * shoulderBias * (1 - openFieldBlend * 0.24)
      + laneBias,
  );
  return { x: targetX, y: targetY };
}

function runFrontEngagementTarget(
  defender: { x: number; y: number },
  blocker: { x: number; y: number },
  role: string,
  direction: number,
  flowKey: string,
  targetY: number,
  carrierY: number,
  openFieldBlend: number = 0,
): { x: number; y: number } {
  const roleIndex = trenchRoleIndex(role);
  const outsideSign = flowKey === "top" ? -1 : flowKey === "bottom" ? 1 : roleIndex < 0 ? -1 : roleIndex > 0 ? 1 : 0;
  const edgeRole = role.startsWith("DE");
  const shoulderBias = edgeRole ? 7 : 4;
  const targetX = forwardOnlyTargetX(
    defender.x,
    snapToPixel(blocker.x + direction * (edgeRole ? 12 : 10)),
    direction,
  );
  const targetYValue = snapToPixel(
    lerp(blocker.y, targetY, 0.7)
      + outsideSign * shoulderBias * (1 - openFieldBlend * 0.24)
      + Math.sign(carrierY - blocker.y) * (openFieldBlend > 0.18 ? 2 : 1),
  );
  return { x: targetX, y: targetYValue };
}

export function runPursuitLaneTarget(
  carrier: { x: number; y: number; vx?: number; vy?: number },
  roleMode: "primary" | "cleanup" | "force" | "backside" | "trail",
  flowKey: string,
  direction: number,
  closeOffsetX: number,
  closeOffsetY: number,
  openFieldBlend: number = 0,
): { x: number; y: number } {
  const baseVx = Math.abs(carrier.vx || 0) > 0.01 || Math.abs(carrier.vy || 0) > 0.01
    ? (carrier.vx || 0)
    : direction;
  const baseVy = Math.abs(carrier.vx || 0) > 0.01 || Math.abs(carrier.vy || 0) > 0.01
    ? (carrier.vy || 0)
    : 0;
  const magnitude = Math.max(0.001, Math.hypot(baseVx, baseVy));
  const forward = { x: baseVx / magnitude, y: baseVy / magnitude };
  const normal = { x: -forward.y, y: forward.x };
  const outsideSign = flowKey === "top"
    ? -1
    : flowKey === "bottom"
      ? 1
      : Math.sign(closeOffsetY || 1) || 1;

  const profile = roleMode === "primary"
    ? { lead: 10 + openFieldBlend * 4, lateral: outsideSign * 1.5, offsetScale: 0.16 }
    : roleMode === "cleanup"
      ? { lead: 8 + openFieldBlend * 3, lateral: -outsideSign * (8 + openFieldBlend * 5), offsetScale: 0.22 }
      : roleMode === "force"
        ? { lead: 16 + openFieldBlend * 5, lateral: outsideSign * (16 + openFieldBlend * 6), offsetScale: 0.28 }
        : roleMode === "backside"
          ? { lead: 6 + openFieldBlend * 2, lateral: -outsideSign * (14 + openFieldBlend * 4), offsetScale: 0.24 }
          : { lead: 6 + openFieldBlend * 2, lateral: -outsideSign * 4, offsetScale: 0.18 };

  return {
    x: snapToPixel(carrier.x + forward.x * profile.lead + normal.x * profile.lateral + direction * closeOffsetX * profile.offsetScale),
    y: snapToPixel(carrier.y + forward.y * profile.lead + normal.y * profile.lateral + closeOffsetY * profile.offsetScale),
  };
}

function capRunScale(scale: number, allowCollisionBurst: boolean = false): number {
  return Math.min(scale, allowCollisionBurst ? 1.12 : 1);
}

function runTackleSideSign(tackleSide: string, flowKey: string): number {
  if (tackleSide === "top-hip") return -1;
  if (tackleSide === "bottom-hip") return 1;
  if (flowKey === "top") return 1;
  if (flowKey === "bottom") return -1;
  return 0;
}

function runFinishOffsets(tackleStyle: string, lateralSign: number): {
  primary: { x: number; y: number };
  support: { x: number; y: number };
  force: { x: number; y: number };
  carrier: { x: number; y: number };
} {
  const side = lateralSign === 0 ? 1 : lateralSign;
  switch (tackleStyle) {
    case "secondary-cleanup":
      return {
        primary: { x: -1, y: side * 3 },
        support: { x: 4, y: side * 5 },
        force: { x: 11, y: side * 7 },
        carrier: { x: 3, y: side * 2 },
      };
    case "chased-out":
      return {
        primary: { x: 0, y: side * 5 },
        support: { x: 4, y: side * 7 },
        force: { x: 10, y: side * 8 },
        carrier: { x: 2, y: side * 3 },
      };
    case "ankle-tackle":
      return {
        primary: { x: -2, y: side * 3 },
        support: { x: 2, y: side * 4 },
        force: { x: 8, y: side * 5 },
        carrier: { x: 2, y: 0 },
      };
    case "gang-tackle":
    case "pile-stop":
      return {
        primary: { x: 1, y: side },
        support: { x: 2, y: -side * 2 },
        force: { x: 5, y: side * 2 },
        carrier: { x: 1, y: 0 },
      };
    case "immediate-hit":
    case "in-hole-hit":
      return {
        primary: { x: 2, y: side },
        support: { x: 4, y: side * 2 },
        force: { x: 7, y: side * 2 },
        carrier: { x: 1, y: 0 },
      };
    default:
      return {
        primary: { x: 1, y: side * 2 },
        support: { x: 4, y: side * 4 },
        force: { x: 8, y: side * 5 },
        carrier: { x: 2, y: side },
      };
  }
}

export function buildRunLiveSequence(context: any): any[] {
  if (context.play?.arcadeReplay?.source === 'arcade-sim-v1' && context.play?.arcadePlaybackMode === 'raw') {
    return buildArcadeRunLiveSequence(context);
  }
  context = context.presentationPlay ? { ...context, play: context.presentationPlay } : context;
  if (isFumbleTurnover(context.play)) {
    return buildRunFumbleLiveSequence(context);
  }
  return buildStandardRunLiveSequence(context);
}

function buildStandardRunLiveSequence(context: any): any[] {
  const arcadePresentedInsideZone = context.play?.arcadeReplay?.source === "arcade-sim-v1"
    && context.play?.arcadePlaybackMode !== "raw"
    && context.play?.playId === "inside-zone";
  const presentationPlan = context.presentationPlan || {};
  const runPresentation = presentationPlan.run || {};
  const extendedMode = presentationPlan.mode === "extended";
  const frameCount = Math.max(14, Math.round(liveFrameCount(context.play) * (arcadePresentedInsideZone ? 1.12 : 1)));
  const pressureTiming = context.play.pressureTiming
    || (context.play.pressureLook === "mug" || context.play.pressureLook === "edge" ? "trigger" : "balanced");
  const rallyStyle = context.play.rallyStyle
    || (context.play.pursuitStyle === "overplay" ? "swarm" : context.play.boxCount >= 8 ? "downhill" : "inside-out");
  const tackleEnvironment = context.play.tackleEnvironment
    || (context.play.boxCount >= 8 ? "crowded" : context.play.coverageShell === "two-high" || context.play.coverageShell === "quarters" ? "space" : "leverage");
  const arcadeTempoScale = arcadePresentedInsideZone ? 0.92 : 1;
  const arcadePursuitScale = arcadePresentedInsideZone ? 0.84 : 1;
  const arcadeSpacingScale = arcadePresentedInsideZone ? 1.08 : 1;
  const pursuitMultiplier = (rallyStyle === "swarm" ? 1.14 : rallyStyle === "downhill" ? 1.08 : rallyStyle === "vice" ? 0.9 : 1)
    * clampRange(runPresentation.pursuitSpread ?? 1, 0.84, 1.18)
    * arcadePursuitScale;
  const spacingMultiplier = (tackleEnvironment === "space" ? 1.16 : tackleEnvironment === "crowded" ? 0.84 : tackleEnvironment === "chaotic" ? 0.92 : 1)
    * clampRange(runPresentation.contactIntensity ?? 1, 0.86, 1.14)
    * arcadeSpacingScale;
  const terminalReason = stopReasonForPlay(context.play);
  const gainedYards = (context.play.endYardLine ?? context.play.startYardLine ?? 0) - (context.play.startYardLine ?? 0);
  const negativeRun = context.play.playId !== "kneel" && gainedYards < 0;
  const qbRun = !!context.play.qbRun || context.play.playId === "kneel";
  const qbDraw = context.play.playId === "qb-draw" || context.play.qbRunType === "draw";
  const qbSneak = context.play.playId === "qb-sneak" || context.play.qbRunType === "sneak";
  const perimeterQbRun = context.play.playId === "boot-keeper"
    || context.play.playId === "speed-option"
    || context.play.qbRunType === "keeper"
    || context.play.qbRunType === "perimeter-option";
  const readOptionKeep = !!context.play.readOptionKeep;
  const offenseBase = clonePlayers(context.formation.offense);
  const defenseBase = clonePlayers(context.formation.defense);
  const offenseMotion = new Map(offenseBase.map((player: FieldPlayer) => [player.role, createMotionState(player)]));
  const defenseMotion = new Map(defenseBase.map((player: FieldPlayer) => [player.role, createMotionState(player)]));
  const xDelta = (value: number) => value * context.direction;
  const carrierRole = qbRun
    ? "QB"
    : context.play.participants?.runnerRole === "FB"
      ? "FB"
      : context.play.participants?.runnerRole === "WR"
        ? "SLOT"
        : "RB";
  const leadRole = String(runPresentation.leadRole || (context.play.readOptionKeep ? "TE" : "SLOT"));
  const tackleStyle = String(context.play.tackleStyle || "wrap-up");
  const recordedTackleDepth = Math.max(0, Number(context.play.runTacklerDepthYards) || 0);
  const arrivalJitterFrames = runPresentation.arrivalJitterFrames ?? 0;
  const carrierBase = findPlayer(offenseBase, carrierRole) || findPlayer(offenseBase, "RB") || offenseBase[0];
  const qbBase = findPlayer(offenseBase, "QB") || carrierBase;
  const rbBase = findPlayer(offenseBase, "RB") || carrierBase;
  const centerBase = findPlayer(offenseBase, "C") || qbBase;
  const snapFrameCount = clampRange(Math.round(frameCount * 0.1), 3, 5);
  const meshPoint = {
    x: orientX(context.losX, -44, context.direction),
    y: snapToPixel(lerp(qbBase.y, rbBase.y, 0.58)),
  };
  const drawSetPoint = { x: orientX(context.losX, -28, context.direction), y: context.centerY - 4 };
  const sneakPoint = { x: orientX(context.losX, -20, context.direction), y: context.centerY - 4 };
  const edgeKeepPoint = { x: orientX(context.losX, -36, context.direction), y: snapToPixel(lerp(qbBase.y, context.laneY, 0.35)) };
  const handoffX = context.play.playId === "kneel"
    ? carrierBase.x
    : qbSneak
      ? sneakPoint.x
    : qbDraw
      ? drawSetPoint.x
      : perimeterQbRun
        ? edgeKeepPoint.x
      : readOptionKeep
        ? meshPoint.x
        : orientX(context.losX, -62, context.direction);
  const handoffY = context.play.playId === "kneel"
    ? carrierBase.y
    : qbSneak
      ? sneakPoint.y
    : qbDraw
      ? drawSetPoint.y
      : perimeterQbRun
        ? edgeKeepPoint.y
      : readOptionKeep
        ? meshPoint.y
        : context.centerY + 10;
  const carrierLaneY = negativeRun ? lossRunLaneY(context.play, handoffY, context.laneY) : context.laneY;
  const pressX = orientX(context.losX, qbSneak ? 6 : qbDraw ? 14 : perimeterQbRun ? 20 : qbRun ? 10 : 6, context.direction);
  const pressY = snapToPixel(lerp(handoffY, carrierLaneY, qbSneak ? 0.3 : qbDraw ? 0.56 : perimeterQbRun ? 0.62 : qbRun ? 0.48 : 0.44));
  const terminalPoint = { x: context.endX, y: carrierLaneY };
  const throughDistance = terminalReason === "tackle"
    ? negativeRun
      ? 8
      : clampRange(10 + Math.max(0, gainedYards - 8) * 0.6 + (runPresentation.fallForwardDistance ?? 0), 12, extendedMode ? 24 : 20)
    : 0;
  const throughPoint = terminalReason === "tackle"
    ? {
        x: snapToPixel(terminalPoint.x + xDelta(throughDistance)),
        y: snapToPixel(terminalPoint.y + (negativeRun ? (carrierLaneY < context.centerY ? -2 : carrierLaneY > context.centerY ? 2 : 0) : 0)),
      }
    : terminalPoint;
  const explosiveRun = terminalReason === "tackle" && !negativeRun && (gainedYards >= 10 || extendedMode || recordedTackleDepth >= 8);
  const guideTerminalPoint = explosiveRun ? throughPoint : terminalPoint;
  const carrierGuide = context.play.playId === "kneel"
    ? compactPathPoints([
        { x: carrierBase.x, y: carrierBase.y },
        { x: orientX(context.losX, -58, context.direction), y: context.centerY + 2 },
        terminalPoint,
      ])
    : qbDraw
      ? compactPathPoints([
          { x: carrierBase.x, y: carrierBase.y },
          drawSetPoint,
          { x: pressX, y: pressY },
          { x: snapToPixel(lerp(pressX, guideTerminalPoint.x, 0.44)), y: snapToPixel(lerp(pressY, guideTerminalPoint.y, 0.4)) },
          guideTerminalPoint,
        ])
      : qbSneak
        ? compactPathPoints([
            { x: carrierBase.x, y: carrierBase.y },
            sneakPoint,
            guideTerminalPoint,
          ])
      : perimeterQbRun
        ? compactPathPoints([
            { x: carrierBase.x, y: carrierBase.y },
            edgeKeepPoint,
            { x: pressX, y: pressY },
            { x: snapToPixel(lerp(pressX, guideTerminalPoint.x, 0.48)), y: snapToPixel(lerp(pressY, guideTerminalPoint.y, 0.4)) },
            guideTerminalPoint,
          ])
      : readOptionKeep
        ? compactPathPoints([
            { x: carrierBase.x, y: carrierBase.y },
            meshPoint,
            { x: pressX, y: pressY },
            guideTerminalPoint,
          ])
    : compactPathPoints([
        { x: carrierBase.x, y: carrierBase.y },
        ...(negativeRun
          ? buildLossRunWaypoints(
              context.play,
              carrierBase.x,
              carrierBase.y,
              handoffX,
              handoffY,
              context.endX,
              carrierLaneY,
              context.direction
            )
          : buildRunWaypoints(
              context.play,
              carrierBase.x,
              carrierBase.y,
              handoffX,
              handoffY,
              pressX,
              pressY,
              context.endX,
              context.laneY,
              context.centerY,
              context.direction
            )),
        guideTerminalPoint,
      ]);
  const handoffFrame = context.play.playId === "kneel"
    ? 0
    : qbSneak
      ? Math.max(2, Math.round(frameCount * 0.08))
    : qbDraw
      ? Math.max(6, Math.round(frameCount * 0.22))
      : perimeterQbRun
        ? Math.max(5, Math.round(frameCount * 0.16))
      : readOptionKeep
        ? Math.max(5, Math.round(frameCount * 0.18))
        : Math.max(4, Math.round(frameCount * 0.16));
  const handoffBlendFrames = carrierRole === "QB" ? 0 : Math.max(2, Math.min(4, handoffFrame - snapFrameCount));
  const handoffBlendStart = carrierRole === "QB" ? handoffFrame : Math.max(snapFrameCount, handoffFrame - handoffBlendFrames);
  const readEnd = Math.max(
    handoffFrame + 4,
    Math.round(frameCount * 0.34)
      + (pressureTiming === "trigger" ? -2 : pressureTiming === "delayed" ? 2 : 0)
      + (rallyStyle === "downhill" ? -1 : rallyStyle === "vice" ? 1 : 0),
  );
  const commitStart = Math.max(
    readEnd + 3,
    Math.round(frameCount * 0.52)
      + (rallyStyle === "swarm" || rallyStyle === "downhill" ? -3 : rallyStyle === "vice" ? 2 : 0)
      + (tackleEnvironment === "crowded" ? -2 : tackleEnvironment === "space" ? 2 : 0),
  );
  const longGain = terminalReason === "tackle" && !negativeRun && (gainedYards >= 12 || extendedMode);
  const openFieldTriggerProgress = clampRange(runPresentation.openFieldTriggerProgress ?? (explosiveRun ? 0.46 : 0.56), 0.32, 0.72);
  const openFieldTriggerYards = clampRange(runPresentation.openFieldTriggerYards ?? (explosiveRun ? 6 : 5), 3, 10);
  const trenchFadeScale = clampRange(runPresentation.trenchFadeScale ?? (extendedMode ? 0.34 : 0.44), 0.22, 0.56);
  const backgroundTrailDistance = clampRange(runPresentation.backgroundTrailDistance ?? (explosiveRun ? 34 : 24), 18, 52);
  const pursuitFocusBoost = clampRange(runPresentation.pursuitFocusBoost ?? 1.08, 0.96, 1.24);
  const finalApproachDistance = clampRange(runPresentation.finalApproachDistance ?? (recordedTackleDepth >= 8 ? 14 : tackleEnvironment === "space" ? 12 : 10), 8, 18);
  const finisherBufferFrames = clampRange(runPresentation.finisherBufferFrames ?? (explosiveRun ? 8 : 6), 4, 12);
  const carrierGuideDistance = pathDistance(carrierGuide);
  const carrierGuidePace = playerPixelsPerFrame(
    carrierBase.speedRating || 84,
    "run",
    terminalReason === "tackle"
      ? negativeRun
        ? 0.68
        : longGain
          ? 0.7
          : 0.74
      : 0.88,
  ) * arcadeTempoScale;
  const minGuideFrames = Math.max(
    handoffFrame + 8,
    Math.ceil(carrierGuideDistance / Math.max(1.8, carrierGuidePace)),
  );
  const guidePaddingFrames = terminalReason === "tackle"
    ? (extendedMode ? (runPresentation.extendedWindowFrames ?? 0) : 0)
    : terminalReason === "touchdown"
      ? (gainedYards >= 16 ? 4 : 2)
      : terminalReason === "out-of-bounds"
        ? (gainedYards >= 10 ? 3 : 2)
        : 0;
  const baseGuideFrames = terminalReason === "tackle"
    ? Math.max(handoffFrame + 8, frameCount - (negativeRun ? 3 : 4))
    : Math.max(handoffFrame + 8, frameCount - 1);
  const guideFrames = Math.max(
    baseGuideFrames,
    minGuideFrames + guidePaddingFrames,
  );
  const tackleTailFrames = terminalReason === "tackle"
    ? Math.max(
        8,
        (negativeRun ? 10 : 14)
          + (tackleEnvironment === "space" ? 3 : tackleEnvironment === "crowded" ? -2 : 0)
          + (rallyStyle === "swarm" ? -1 : rallyStyle === "vice" ? 1 : 0),
      )
    : 4;
  const totalFrames = Math.max(frameCount + tackleTailFrames, guideFrames + tackleTailFrames);

  const runSupportTargets: Record<string, { x: number; y: number }> = {
    TE: { x: orientX(context.losX, 18, context.direction), y: context.centerY - 92 },
    WR1: { x: orientX(context.losX, 26, context.direction), y: context.centerY - 148 },
    WR2: { x: orientX(context.losX, 26, context.direction), y: context.centerY + 146 },
    SLOT: { x: orientX(context.losX, 20, context.direction), y: context.centerY + 92 },
    LT: { x: orientX(context.losX, 4, context.direction), y: context.centerY - 70 },
    LG: { x: orientX(context.losX, 6, context.direction), y: context.centerY - 36 },
    C: { x: orientX(context.losX, 4, context.direction), y: context.centerY - 2 },
    RG: { x: orientX(context.losX, 6, context.direction), y: context.centerY + 32 },
    RT: { x: orientX(context.losX, 4, context.direction), y: context.centerY + 66 },
  };

  const flowKey = context.play.playId === "kneel"
    ? "middle"
    : context.laneY < context.centerY - 28
      ? "top"
      : context.laneY > context.centerY + 28
        ? "bottom"
        : "middle";
  const runFamily = resolvedRunFamily(context.play);
  const playId = String(context.play.playId || "");
  const perimeterRun = runFamily === "toss"
    || runFamily === "option"
    || playId === "outside-zone"
    || playId === "crack-toss"
    || playId === "boot-keeper";
  const downhillRun = runFamily === "power" || runFamily === "duo" || runFamily === "trap";
  const counterRun = runFamily === "counter";
  const carrierSample = context.play.playId === "kneel" || qbSneak
    ? "polyline"
    : negativeRun && !perimeterRun && !counterRun && !qbDraw
      ? "polyline"
      : "spline";
  const playsideEdgeRole = flowKey === "top" ? "DE1" : flowKey === "bottom" ? "DE2" : null;
  const backsideEdgeRole = flowKey === "top" ? "DE2" : flowKey === "bottom" ? "DE1" : null;
  const playsideInteriorRole = flowKey === "top" ? "DT1" : flowKey === "bottom" ? "DT2" : "DT1";
  const backsideInteriorRole = flowKey === "top" ? "DT2" : flowKey === "bottom" ? "DT1" : "DT2";
  const frontFitJob = (role: string): string => {
    if (flowKey === "middle") {
      if (role === "DE1" || role === "DE2") return perimeterRun ? "force" : downhillRun ? "spill" : "box";
      if (role === "DT1") return downhillRun ? "dent" : counterRun ? "spill" : "dent";
      if (role === "DT2") return counterRun ? "lag" : perimeterRun ? "trail" : "dent";
      return "trail";
    }
    if (role === playsideEdgeRole) return perimeterRun ? "force" : downhillRun ? "spill" : counterRun ? "box" : "box";
    if (role === backsideEdgeRole) return counterRun ? "retrace" : "cutback";
    if (role === playsideInteriorRole) return perimeterRun ? "spill" : downhillRun ? "dent" : counterRun ? "spill" : "dent";
    if (role === backsideInteriorRole) return counterRun ? "lag" : perimeterRun ? "trail" : "lag";
    return "trail";
  };

  const frontTargets: Record<string, { x: number; y: number }> = {
    top: {
      DE1: { x: orientX(context.losX, 10, context.direction), y: context.centerY - 102 },
      DT1: { x: orientX(context.losX, 2, context.direction), y: context.centerY - 40 },
      DT2: { x: orientX(context.losX, -2, context.direction), y: context.centerY + 14 },
      DE2: { x: orientX(context.losX, 0, context.direction), y: context.centerY + 62 },
    },
    middle: {
      DE1: { x: orientX(context.losX, 6, context.direction), y: context.centerY - 72 },
      DT1: { x: orientX(context.losX, 0, context.direction), y: context.centerY - 26 },
      DT2: { x: orientX(context.losX, 0, context.direction), y: context.centerY + 18 },
      DE2: { x: orientX(context.losX, 6, context.direction), y: context.centerY + 68 },
    },
    bottom: {
      DE1: { x: orientX(context.losX, 0, context.direction), y: context.centerY - 62 },
      DT1: { x: orientX(context.losX, -2, context.direction), y: context.centerY - 18 },
      DT2: { x: orientX(context.losX, 2, context.direction), y: context.centerY + 36 },
      DE2: { x: orientX(context.losX, 10, context.direction), y: context.centerY + 100 },
    },
  }[flowKey] as Record<string, { x: number; y: number }>;

  Object.entries(frontTargets).forEach(([role, target]: [string, { x: number; y: number }]) => {
    if (pressureTiming === "trigger" || rallyStyle === "downhill") {
      target.x = snapToPixel(target.x + xDelta(role.startsWith("DE") ? 6 : 4));
    } else if (pressureTiming === "delayed") {
      target.x = snapToPixel(target.x - xDelta(role.startsWith("DE") ? 4 : 2));
    }

    if (rallyStyle === "swarm" && role.startsWith("DE")) {
      target.y = snapToPixel(lerp(target.y, context.laneY, 0.16));
    }
  });

  const secondLevelPlans: Record<string, any> = {
    top: {
      LB1: { react: Math.round(frameCount * 0.16), read: { x: orientX(context.losX, 32, context.direction), y: context.centerY - 84 }, fit: { x: orientX(context.losX, 58, context.direction), y: context.centerY - 102 }, closeOffsetX: 18, closeOffsetY: -24, scale: 0.92, mode: "primary" },
      LB2: { react: Math.round(frameCount * 0.14), read: { x: orientX(context.losX, 36, context.direction), y: context.centerY - 4 }, fit: { x: orientX(context.losX, 50, context.direction), y: context.centerY - 28 }, closeOffsetX: 18, closeOffsetY: -8, scale: 0.86, mode: "support" },
      LB3: { react: Math.round(frameCount * 0.2), read: { x: orientX(context.losX, 34, context.direction), y: context.centerY + 80 }, fit: { x: orientX(context.losX, 42, context.direction), y: context.centerY + 46 }, closeOffsetX: 42, closeOffsetY: 30, scale: 0.72, mode: "backside" },
      CB1: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY - 146 }, fit: { x: orientX(context.losX, 104, context.direction), y: context.centerY - 156 }, closeOffsetX: 34, closeOffsetY: -56, scale: 0.8, mode: "force" },
      CB2: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY + 142 }, fit: { x: orientX(context.losX, 88, context.direction), y: context.centerY + 116 }, closeOffsetX: 54, closeOffsetY: 54, scale: 0.64, mode: "backside" },
      S1: { react: Math.round(frameCount * 0.22), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY - 48 }, fit: { x: orientX(context.losX, 82, context.direction), y: context.centerY - 92 }, closeOffsetX: 24, closeOffsetY: -16, scale: 0.88, mode: "support" },
      S2: { react: Math.round(frameCount * 0.24), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY + 44 }, fit: { x: orientX(context.losX, 94, context.direction), y: context.centerY + 30 }, closeOffsetX: 40, closeOffsetY: 28, scale: 0.7, mode: "backside" },
    },
    middle: {
      LB1: { react: Math.round(frameCount * 0.16), read: { x: orientX(context.losX, 32, context.direction), y: context.centerY - 84 }, fit: { x: orientX(context.losX, 48, context.direction), y: context.centerY - 48 }, closeOffsetX: 20, closeOffsetY: -18, scale: 0.84, mode: "support" },
      LB2: { react: Math.round(frameCount * 0.12), read: { x: orientX(context.losX, 36, context.direction), y: context.centerY - 4 }, fit: { x: orientX(context.losX, 52, context.direction), y: context.centerY - 2 }, closeOffsetX: 16, closeOffsetY: -4, scale: 0.94, mode: "primary" },
      LB3: { react: Math.round(frameCount * 0.16), read: { x: orientX(context.losX, 32, context.direction), y: context.centerY + 80 }, fit: { x: orientX(context.losX, 48, context.direction), y: context.centerY + 44 }, closeOffsetX: 20, closeOffsetY: 16, scale: 0.84, mode: "support" },
      CB1: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY - 146 }, fit: { x: orientX(context.losX, 94, context.direction), y: context.centerY - 112 }, closeOffsetX: 34, closeOffsetY: -46, scale: 0.74, mode: "force" },
      CB2: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY + 142 }, fit: { x: orientX(context.losX, 94, context.direction), y: context.centerY + 108 }, closeOffsetX: 34, closeOffsetY: 44, scale: 0.74, mode: "force" },
      S1: { react: Math.round(frameCount * 0.22), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY - 48 }, fit: { x: orientX(context.losX, 88, context.direction), y: context.centerY - 60 }, closeOffsetX: 24, closeOffsetY: -18, scale: 0.8, mode: "support" },
      S2: { react: Math.round(frameCount * 0.22), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY + 44 }, fit: { x: orientX(context.losX, 88, context.direction), y: context.centerY + 54 }, closeOffsetX: 24, closeOffsetY: 16, scale: 0.8, mode: "support" },
    },
    bottom: {
      LB1: { react: Math.round(frameCount * 0.2), read: { x: orientX(context.losX, 34, context.direction), y: context.centerY - 84 }, fit: { x: orientX(context.losX, 42, context.direction), y: context.centerY - 46 }, closeOffsetX: 42, closeOffsetY: -28, scale: 0.72, mode: "backside" },
      LB2: { react: Math.round(frameCount * 0.14), read: { x: orientX(context.losX, 36, context.direction), y: context.centerY - 4 }, fit: { x: orientX(context.losX, 50, context.direction), y: context.centerY + 22 }, closeOffsetX: 18, closeOffsetY: 10, scale: 0.86, mode: "support" },
      LB3: { react: Math.round(frameCount * 0.16), read: { x: orientX(context.losX, 32, context.direction), y: context.centerY + 80 }, fit: { x: orientX(context.losX, 58, context.direction), y: context.centerY + 100 }, closeOffsetX: 18, closeOffsetY: 24, scale: 0.92, mode: "primary" },
      CB1: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY - 146 }, fit: { x: orientX(context.losX, 88, context.direction), y: context.centerY - 118 }, closeOffsetX: 54, closeOffsetY: -54, scale: 0.64, mode: "backside" },
      CB2: { react: Math.round(frameCount * 0.28), read: { x: orientX(context.losX, 92, context.direction), y: context.centerY + 142 }, fit: { x: orientX(context.losX, 104, context.direction), y: context.centerY + 152 }, closeOffsetX: 34, closeOffsetY: 56, scale: 0.8, mode: "force" },
      S1: { react: Math.round(frameCount * 0.24), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY - 48 }, fit: { x: orientX(context.losX, 94, context.direction), y: context.centerY - 34 }, closeOffsetX: 40, closeOffsetY: -28, scale: 0.7, mode: "backside" },
      S2: { react: Math.round(frameCount * 0.22), read: { x: orientX(context.losX, 108, context.direction), y: context.centerY + 44 }, fit: { x: orientX(context.losX, 82, context.direction), y: context.centerY + 90 }, closeOffsetX: 24, closeOffsetY: 16, scale: 0.88, mode: "support" },
    },
  }[flowKey] as Record<string, any>;

  Object.entries(secondLevelPlans).forEach(([role, plan]: [string, any]) => {
    if (pressureTiming === "trigger") {
      plan.react = Math.max(handoffFrame + 1, plan.react - 2);
      if (role.startsWith("LB") || role.startsWith("S")) {
        plan.read.x = snapToPixel(plan.read.x + xDelta(4));
      }
    } else if (pressureTiming === "delayed") {
      plan.react += 2;
      if (role.startsWith("S")) {
        plan.read.x = snapToPixel(plan.read.x - xDelta(8));
      }
    }

    if (rallyStyle === "downhill") {
      plan.react = Math.max(handoffFrame + 1, plan.react - 1);
      plan.fit.x = snapToPixel(plan.fit.x + xDelta(role.startsWith("S") ? 10 : 8));
      plan.closeOffsetX = Math.max(12, plan.closeOffsetX - 4);
      plan.scale *= role.startsWith("CB") ? 0.96 : 1.08;
    } else if (rallyStyle === "swarm") {
      plan.react = Math.max(handoffFrame + 1, plan.react - 3);
      plan.fit = {
        x: snapToPixel(lerp(plan.fit.x, context.endX, 0.14)),
        y: snapToPixel(lerp(plan.fit.y, context.laneY, 0.22)),
      };
      plan.closeOffsetX = Math.max(12, plan.closeOffsetX - 4);
      plan.scale *= 1.12;
    } else if (rallyStyle === "vice") {
      plan.react += 2;
      plan.closeOffsetX += 6;
      plan.scale *= 0.92;
      if (role.startsWith("S") || role.startsWith("CB")) {
        plan.read.x = snapToPixel(plan.read.x - xDelta(8));
        plan.fit.x = snapToPixel(plan.fit.x - xDelta(10));
      }
    } else if (rallyStyle === "inside-out" && (role.startsWith("S") || role.startsWith("CB"))) {
      plan.closeOffsetX += 4;
      plan.scale *= 0.96;
    }

    if (tackleEnvironment === "crowded") {
      plan.fit.x = snapToPixel(plan.fit.x + xDelta(6));
      plan.closeOffsetX = Math.max(12, plan.closeOffsetX - 3);
    } else if (tackleEnvironment === "space") {
      plan.fit.x = snapToPixel(plan.fit.x - xDelta(4));
      plan.closeOffsetX += 8;
      plan.scale *= 0.94;
      if (role.startsWith("S")) {
        plan.read.x = snapToPixel(plan.read.x - xDelta(6));
      }
    }
  });

  const runRoleOrder = Object.entries(secondLevelPlans)
    .sort(([leftRole, leftPlan]: [string, any], [rightRole, rightPlan]: [string, any]) => {
      const modeRank: Record<string, number> = { primary: 0, support: 1, force: 2, backside: 3 };
      const leftRank = modeRank[leftPlan.mode] ?? 4;
      const rightRank = modeRank[rightPlan.mode] ?? 4;
      return leftRank === rightRank ? leftRole.localeCompare(rightRole) : leftRank - rightRank;
    })
    .map(([role]: [string, any]) => role);

  const negativeSupportRole = ({ top: "LB1", middle: "LB2", bottom: "LB3" } as Record<string, string>)[flowKey] || "LB2";
  const lossFrontRushRoles = negativeRun ? (({ top: ["DE1"], middle: ["DT1"], bottom: ["DE2"] } as Record<string, string[]>)[flowKey] || ["DT1"]) : [];
  let primaryRole = negativeRun ? lossFrontRushRoles[0] : runRoleOrder.find((role: string) => secondLevelPlans[role].mode === "primary") || "LB2";
  let cleanupRole = negativeRun
    ? negativeSupportRole
    : runRoleOrder.find((role: string) => role !== primaryRole && secondLevelPlans[role].mode === "support")
      || runRoleOrder.find((role: string) => role !== primaryRole && secondLevelPlans[role].mode === "force")
      || primaryRole;
  const forceRole = runRoleOrder.find((role: string) => secondLevelPlans[role].mode === "force") || cleanupRole;
  const backsideRole = runRoleOrder.find((role: string) => secondLevelPlans[role].mode === "backside") || forceRole;
  const recordedTacklerRole = context.play.participants?.tacklerRole;
  if (recordedTacklerRole && (secondLevelPlans[recordedTacklerRole] || lossFrontRushRoles.includes(recordedTacklerRole) || ["DT1", "DT2", "DE1", "DE2"].includes(recordedTacklerRole))) {
    cleanupRole = primaryRole;
    primaryRole = recordedTacklerRole;
  }

  // Pursuit-based primary: when simulation data indicates a safety makes the
  // tackle (runTacklerDepthYards >= 8), promote S1/S2 to primary. The safety
  // stays deeper during the read/fit phases (50-55% of gain distance from LOS)
  // so physics-based pursuit naturally closes to the carrier without a
  // teleporting direct-lerp.
  const safetyTackleDepth = longGain ? recordedTackleDepth : 0;
  if (safetyTackleDepth >= 8 && (!recordedTacklerRole || recordedTacklerRole === "S1" || recordedTacklerRole === "S2")) {
    const safetyRole = (recordedTacklerRole === "S1" || recordedTacklerRole === "S2")
      ? recordedTacklerRole
      : ["S1", "S2"].find((r: string) => secondLevelPlans[r]);
    if (safetyRole) {
      const pursuitPx = Math.round(gainedYards * 22 * 0.50);
      const readPx    = Math.round(gainedYards * 22 * 0.55);
      secondLevelPlans[safetyRole] = {
        ...secondLevelPlans[safetyRole],
        read: { x: orientX(context.losX, readPx, context.direction), y: secondLevelPlans[safetyRole].read.y },
        fit:  {
          x: orientX(context.losX, pursuitPx, context.direction),
          y: snapToPixel(lerp(secondLevelPlans[safetyRole].fit.y, context.laneY, 0.55)),
        },
      };
      cleanupRole = primaryRole;
      primaryRole = safetyRole;
    }
  }

  const finishRoles = Array.from(new Set([primaryRole, cleanupRole].filter(Boolean)));
  const pressureRoles = Array.from(new Set([primaryRole, cleanupRole, forceRole, ...lossFrontRushRoles].filter(Boolean)));
  const heroDefenseRoles = new Set(
    extendedMode
      ? Array.from(new Set([primaryRole, cleanupRole, forceRole, backsideRole].filter(Boolean))).slice(0, Math.max(3, runPresentation.heroPursuitCount ?? 4))
      : runRoleOrder
  );
  const heroOffenseRoles = new Set([carrierRole, leadRole, "QB"].filter(Boolean));
  const openFieldMode = explosiveRun && !negativeRun;
  const tackleSide = String(runPresentation.tackleSide || "inside");
  const tackleSideSign = runTackleSideSign(tackleSide, flowKey) || (playHash(context.play, "run-finish-side") % 2 === 0 ? -1 : 1);
  const finishOffsets = runFinishOffsets(tackleStyle, tackleSideSign);
  const supportRequired = tackleStyle === "gang-tackle" || tackleStyle === "pile-stop";
  const supportRole = cleanupRole !== primaryRole ? cleanupRole : forceRole !== primaryRole ? forceRole : null;
  const smoothFinishRun = terminalReason === "tackle" && !negativeRun && (gainedYards >= 10 || safetyTackleDepth >= 8 || recordedTackleDepth >= 8);
  const sackFrontOffsets: Record<string, { x: number; y: number }> = {
    DE1: { x: 10, y: -10 },
    DT1: { x: 6, y: -4 },
    DT2: { x: 6, y: 4 },
    DE2: { x: 10, y: 10 },
  };

  const frames: any[] = [];
  let frozenFrame: any = null;
  let contactState: any = null;
  let plannedFrames = totalFrames;
  let lastExtensionScore = Number.POSITIVE_INFINITY;
  let stalledExtensionWindows = 0;

  for (let frame = 0; frame < plannedFrames; frame += 1) {
    if (frozenFrame) {
      frames.push(cloneLiveFrame(frozenFrame));
      continue;
    }

    const carrierMotion = offenseMotion.get(carrierRole) || offenseMotion.get("RB") || createMotionState(carrierBase);
    const qbMotion = offenseMotion.get("QB") || createMotionState(qbBase);
    const carrierProgress = clamp01(frame / Math.max(1, guideFrames));
    const tossSweep = context.play.playId === "toss-sweep";
    const carrierDriftSign = Math.sign(
      flowKey === "top"
        ? -1
        : flowKey === "bottom"
          ? 1
          : (carrierLaneY - handoffY) || (context.laneY - context.centerY) || ((playHash(context.play, "run-flow-drift") % 2 === 0) ? -1 : 1)
    );
    const guideTarget = flowingGuideTarget(carrierGuide, carrierProgress, carrierSample, {
      lookahead: tossSweep ? 0.054 : perimeterRun ? 0.088 : counterRun ? 0.074 : negativeRun ? 0.048 : 0.064,
      settleBlend: tossSweep ? 0.5 : perimeterRun ? 0.66 : counterRun ? 0.62 : negativeRun ? 0.56 : 0.6,
      driftSign: carrierDriftSign,
      driftScale: tossSweep ? 0.82 : perimeterRun ? 1 : counterRun ? 0.72 : negativeRun ? 0.22 : 0.46,
      minDrift: tossSweep ? 0.9 : perimeterRun ? 0.8 : negativeRun ? 0.35 : 0.45,
      maxDrift: tossSweep ? 5.4 : perimeterRun ? 4.6 : counterRun ? 3.2 : negativeRun ? 1.8 : 2.4,
      nearDriftDistance: tossSweep ? 6 : perimeterRun ? 8 : negativeRun ? 10 : 9,
      farDriftDistance: tossSweep ? 56 : perimeterRun ? 44 : counterRun ? 34 : negativeRun ? 24 : 28,
    });
    const carrierDepthPx = Math.max(0, (carrierMotion.x - context.losX) * context.direction);
    const openFieldDepthBlend = openFieldMode
      ? clamp01((carrierDepthPx - openFieldTriggerYards * 22) / 44)
      : 0;
    const openFieldProgressBlend = openFieldMode
      ? clamp01((carrierProgress - openFieldTriggerProgress) / Math.max(0.12, 0.94 - openFieldTriggerProgress))
      : 0;
    const openFieldBlend = Math.max(openFieldDepthBlend, openFieldProgressBlend);
    const activePressureRoles = openFieldBlend >= 0.35
      ? Array.from(new Set([primaryRole, cleanupRole, forceRole, backsideRole].filter(Boolean)))
      : pressureRoles;
    const tackleAnchorPoint = {
      x: snapToPixel(lerp(terminalPoint.x, carrierMotion.x, 0.22)),
      y: snapToPixel(lerp(terminalPoint.y, carrierMotion.y, 0.22)),
    };
    const nearestPressure = activePressureRoles
      .map((role: string) => {
        const defender = defenseMotion.get(role);
        return defender ? { role, distance: pointDistance(defender, carrierMotion) } : null;
      })
      .filter(Boolean)
      .sort((left: any, right: any) => left.distance - right.distance)[0];
    const primaryApproachMotion = defenseMotion.get(primaryRole);
    const primaryApproachGap = primaryApproachMotion ? pointDistance(primaryApproachMotion, carrierMotion) : Number.POSITIVE_INFINITY;
    const finishApproachBlend = smoothFinishRun && !contactState
      ? clamp01(
          Math.max(
            carrierProgress >= 0.7 ? 0.2 : 0,
            (Math.max(0, 64 - primaryApproachGap) / 40),
            (Math.max(0, 72 - pointDistance(carrierMotion, terminalPoint)) / 44),
          )
        )
      : 0;
    const pressureSlow = contactState
      ? 0.26
      : terminalReason === "tackle"
        ? clampRange(
            clampRange(((nearestPressure?.distance ?? 40) - (negativeRun ? 7 : 10)) / (negativeRun ? 15 : 24), 0.66, 1)
              * (pressureTiming === "trigger" ? 0.92 : pressureTiming === "delayed" ? 1.06 : 1)
              * (tackleEnvironment === "crowded" ? 0.94 : tackleEnvironment === "space" ? 1.04 : 1),
            0.58,
            1.06,
          )
        : 1;

    if (context.play.playId === "kneel") {
      steerMotionState(carrierMotion, guideTarget, { family: "run", scale: 0.48, accelRatio: 0.26, inertia: 0.92, arriveDistance: 12 });
    } else if (frame < handoffFrame) {
      steerMotionState(qbMotion, { x: orientX(context.losX, -56, context.direction), y: context.centerY - 2 }, { family: "run", scale: 0.62, accelRatio: 0.28, inertia: 0.93, arriveDistance: 12 });
      if (carrierRole !== "QB") {
        steerMotionState(carrierMotion, { x: handoffX, y: handoffY }, { family: "run", scale: 0.72, accelRatio: 0.3, inertia: 0.93, arriveDistance: 12 });
      }
    } else if (contactState) {
      const primaryMotion = defenseMotion.get(contactState.primaryRole) || carrierMotion;
      const contactPoint = {
        x: snapToPixel(lerp(tackleAnchorPoint.x, primaryMotion.x, 0.34) + xDelta(finishOffsets.carrier.x)),
        y: snapToPixel(lerp(tackleAnchorPoint.y, primaryMotion.y, 0.42) + finishOffsets.carrier.y),
      };
      steerMotionState(carrierMotion, contactPoint, {
        family: "run",
        scale: negativeRun ? 0.34 : openFieldBlend > 0.28 ? 0.48 : 0.42,
        accelRatio: 0.26,
        inertia: 0.88,
        arriveDistance: 10,
      });
      if (carrierRole !== "QB") {
        steerMotionState(qbMotion, { x: orientX(context.losX, -56, context.direction), y: context.centerY - 2 }, { family: "run", scale: 0.44, accelRatio: 0.24, inertia: 0.9, arriveDistance: 10 });
      }
    } else {
      const carrierTarget = finishApproachBlend > 0
        ? {
            x: snapToPixel(lerp(guideTarget.x, terminalPoint.x, 0.22 + finishApproachBlend * 0.18)),
            y: snapToPixel(lerp(guideTarget.y, terminalPoint.y, 0.18 + finishApproachBlend * 0.16)),
          }
        : guideTarget;
      steerMotionState(carrierMotion, carrierTarget, {
        family: "run",
        scale: (negativeRun ? 0.76 : openFieldBlend > 0.18 ? 0.92 : 0.88)
          * pressureSlow
          * (finishApproachBlend > 0 ? lerp(1, 0.86, finishApproachBlend) : 1),
        accelRatio: negativeRun ? 0.28 : 0.32,
        inertia: 0.95,
        arriveDistance: negativeRun ? 14 : 18,
      });
      if (carrierRole !== "QB") {
        steerMotionState(qbMotion, { x: orientX(context.losX, -56, context.direction), y: context.centerY - 2 }, { family: "run", scale: 0.44, accelRatio: 0.24, inertia: 0.9, arriveDistance: 10 });
      }
    }

    Object.entries(runSupportTargets).forEach(([role, target]: [string, { x: number; y: number }], supportIndex: number) => {
      if (!offenseMotion.has(role) || role === carrierRole || (carrierRole === "QB" && role === "QB")) {
        return;
      }
      const motion = offenseMotion.get(role)!;
      const defenderRole = offensiveBattlePartnerRole(role);
      const defenderMotion = defenderRole ? defenseMotion.get(defenderRole) : null;
      const isTrenchRole = isOffensiveTrenchRole(role);
      const isPerimeterRole = role.startsWith("WR") || role === "SLOT" || role === "TE";
      const trenchIndex = role === "LT" ? -2 : role === "LG" ? -1 : role === "C" ? 0 : role === "RG" ? 1 : role === "RT" ? 2 : 0;
      const playsideBias = flowKey === "top"
        ? Math.max(0, -trenchIndex)
        : flowKey === "bottom"
          ? Math.max(0, trenchIndex)
          : Math.max(0, 1 - Math.abs(trenchIndex));
      const backsideBias = flowKey === "top"
        ? Math.max(0, trenchIndex)
        : flowKey === "bottom"
          ? Math.max(0, -trenchIndex)
          : Math.max(0, Math.abs(trenchIndex) - 1);
      const roleBand = target.y < context.centerY ? -1 : target.y > context.centerY ? 1 : 0;
      const backgroundBlend = Math.max(extendedMode ? 0.4 : 0, openFieldBlend);
      const trenchAnchor = {
        x: snapToPixel(forwardOnlyTargetX(
          motion.x,
          orientX(context.losX, 6 + playsideBias * 6 + Math.max(0, 1 - backsideBias) * 2, context.direction),
          context.direction,
        )),
        y: snapToPixel(
          target.y
            + (flowKey === "top" ? -1 : flowKey === "bottom" ? 1 : 0) * (playsideBias * 16 + backsideBias * 6)
        ),
      };
      const trenchEngagement = isTrenchRole && defenderMotion && backgroundBlend <= 0.22
        ? runTrenchEngagementTarget(motion, defenderMotion, role, context.direction, flowKey, carrierMotion.y, openFieldBlend)
        : null;
      const leadTarget = role === leadRole
        ? {
            x: snapToPixel(carrierMotion.x + xDelta(openFieldBlend > 0.56 ? 14 : 18)),
            y: snapToPixel(carrierMotion.y + (flowKey === "top" ? -10 : flowKey === "bottom" ? 10 : 0) + openFieldBlend * tackleSideSign * 4),
          }
        : trenchEngagement
          ? trenchEngagement
        : isTrenchRole && backgroundBlend > 0.12
          ? {
              x: trenchAnchor.x,
              y: snapToPixel(lerp(trenchAnchor.y, carrierMotion.y, 0.04)),
            }
        : backgroundBlend > 0.12 && !heroOffenseRoles.has(role)
          ? {
              x: snapToPixel(carrierMotion.x - xDelta(backgroundTrailDistance + supportIndex * 5 + (isTrenchRole ? 10 : isPerimeterRole ? 0 : 4))),
              y: snapToPixel(
                carrierMotion.y
                  + roleBand * (isTrenchRole ? 18 : isPerimeterRole ? 42 : 26) * backgroundBlend
                  + ((supportIndex % 3) - 1) * (isTrenchRole ? 6 : 10) * (runPresentation.backgroundDriftScale ?? 0.54)
              ),
            }
          : {
              x: target.x,
              y: snapToPixel(lerp(target.y, carrierMotion.y, role.startsWith("W") || role === "TE" || role === "SLOT" ? 0.08 : 0.04)),
            };
      steerMotionState(motion, leadTarget, {
        family: "run",
        scale: role === leadRole
          ? (openFieldBlend > 0.56 ? 0.66 : 0.74)
          : trenchEngagement
            ? 0.48
          : isTrenchRole && backgroundBlend > 0.12
            ? 0.34
          : backgroundBlend > 0.12 && !heroOffenseRoles.has(role)
            ? (isTrenchRole ? 0.34 : isPerimeterRole ? 0.5 : 0.42)
            : role.startsWith("W") || role === "TE" || role === "SLOT"
              ? 0.66
              : 0.62,
        accelRatio: trenchEngagement ? 0.24 : 0.28,
        inertia: trenchEngagement ? 0.94 : 0.92,
        arriveDistance: trenchEngagement ? 12 : 16,
      });
    });

    Object.entries(frontTargets).forEach(([role, target]: [string, { x: number; y: number }]) => {
      const motion = defenseMotion.get(role);
      if (!motion) {
        return;
      }
      const partnerRole = frontBattlePartnerRole(role);
      const partnerMotion = partnerRole ? offenseMotion.get(partnerRole) : null;
      const fitJob = frontFitJob(role);
      const frontBand = target.y < context.centerY ? -1 : target.y > context.centerY ? 1 : 0;
      const trenchHoldPoint = {
        x: snapToPixel(forwardOnlyTargetX(
          motion.x,
          orientX(context.losX, role.startsWith("DE") ? 10 : 6, context.direction),
          context.direction,
        )),
        y: snapToPixel(target.y + frontBand * openFieldBlend * 10),
      };
      const battleAnchor = partnerMotion
        ? runFrontEngagementTarget(motion, partnerMotion, role, context.direction, flowKey, target.y, carrierMotion.y, openFieldBlend)
        : trenchHoldPoint;
      const trenchBattleBlend = !negativeRun && !finishRoles.includes(role) && !perimeterRun
        ? flowKey === "middle"
          ? 0.78 + openFieldBlend * 0.08
          : downhillRun || runFamily === "zone" || counterRun
            ? 0.66 + openFieldBlend * 0.12
            : 0.54
        : 0;
      let desired = target;
      if (frame >= readEnd) {
        const fitPoint = runFrontFitPoint(
          fitJob,
          role,
          target,
          trenchHoldPoint,
          carrierMotion,
          { losX: context.losX, centerY: context.centerY, direction: context.direction },
          flowKey,
          openFieldBlend,
        );
        if (contactState && finishRoles.includes(role)) {
          const offsets = role === primaryRole ? finishOffsets.primary : finishOffsets.support;
          desired = projectedPoint(carrierMotion, 0.4, xDelta(offsets.x), offsets.y);
        } else if (negativeRun && role === primaryRole) {
          const offsets = sackFrontOffsets[role] || { x: 6, y: 0 };
          desired = projectedPoint(carrierMotion, 0.9, xDelta(offsets.x), offsets.y);
        } else if ((extendedMode || openFieldBlend > 0.18) && !finishRoles.includes(role)) {
          desired = {
            x: trenchHoldPoint.x,
            y: snapToPixel(lerp(trenchHoldPoint.y, fitPoint.y, fitJob === "force" || fitJob === "cutback" ? 0.34 : 0.2)),
          };
        } else {
          desired = fitPoint;
        }
      }
      if (trenchBattleBlend > 0) {
        desired = {
          x: snapToPixel(lerp(desired.x, battleAnchor.x, clampRange(trenchBattleBlend, 0.24, 0.88))),
          y: snapToPixel(lerp(desired.y, battleAnchor.y, clampRange(trenchBattleBlend + 0.08, 0.32, 0.92))),
        };
        desired = curvedApproachTarget(motion, desired, frontBand || (role === "DE1" ? -1 : role === "DE2" ? 1 : 0), {
          nearDistance: 8,
          farDistance: 46,
          minOffset: 2,
          maxOffset: role.startsWith("DE") ? 8 : 6,
          scale: 0.64,
        });
      }
      const baseFrontScale = fitJob === "force"
        ? 0.58
        : fitJob === "spill"
          ? 0.54
          : fitJob === "box"
            ? 0.52
            : fitJob === "dent"
              ? 0.48
              : fitJob === "retrace"
                ? 0.44
                : fitJob === "cutback"
                  ? 0.42
                  : 0.38;
      const fadedFrontScale = (fitJob === "force" || fitJob === "cutback" ? trenchFadeScale * 1.04 : trenchFadeScale * 0.92);
      const frontScale = contactState && finishRoles.includes(role)
        ? 0.72 * pursuitMultiplier
        : negativeRun && role === primaryRole
          ? 0.84 * pursuitMultiplier
          : trenchBattleBlend > 0
            ? (0.3 + (role.startsWith("DE") ? 0.02 : 0)) * pursuitMultiplier
          : ((extendedMode || openFieldBlend > 0.18) && !finishRoles.includes(role) ? fadedFrontScale : baseFrontScale) * pursuitMultiplier;
      steerMotionState(motion, desired, {
        family: "run",
        scale: capRunScale(frontScale, contactState && finishRoles.includes(role)),
        accelRatio: 0.28,
        inertia: 0.95,
        arriveDistance: 18,
      });
    });

    runRoleOrder.forEach((role: string, roleIndex: number) => {
      const plan = secondLevelPlans[role];
      const motion = defenseMotion.get(role);
      if (!plan || !motion) {
        return;
      }

      let desired = frame < plan.react
        ? plan.read
        : {
            x: snapToPixel(lerp(plan.read.x, plan.fit.x, clamp01((frame - plan.react) / Math.max(1, readEnd - plan.react)))),
            y: snapToPixel(lerp(plan.read.y, plan.fit.y, clamp01((frame - plan.react) / Math.max(1, readEnd - plan.react)))),
          };

      if (frame >= commitStart) {
        const roleMode = role === primaryRole
          ? "primary"
          : role === cleanupRole
            ? "cleanup"
            : role === forceRole
              ? "force"
              : role === backsideRole
                ? "backside"
                : "trail";
        const leverageScale = negativeRun ? 0.36 : openFieldBlend > 0.1 ? lerp(1, 0.72, openFieldBlend) : 1;
        const primaryCloseBlend = role === primaryRole && smoothFinishRun
          ? clamp01(Math.max(0, 64 - pointDistance(motion, carrierMotion)) / 36)
          : 0;
        const closeLeadX = role === primaryRole
          ? lerp(plan.closeOffsetX * leverageScale, finishOffsets.primary.x, primaryCloseBlend * 0.84)
          : plan.closeOffsetX * leverageScale;
        const closeLeadY = role === primaryRole
          ? lerp(plan.closeOffsetY * leverageScale, finishOffsets.primary.y, primaryCloseBlend * 0.9)
          : plan.closeOffsetY * leverageScale;
        let chasePoint = runPursuitLaneTarget(
          carrierMotion,
          roleMode,
          flowKey,
          context.direction,
          closeLeadX,
          closeLeadY,
          openFieldBlend,
        );

        if (role === primaryRole && primaryCloseBlend > 0) {
          const closePoint = projectedPoint(
            carrierMotion,
            lerp(negativeRun ? 0.9 : 2.3, smoothFinishRun ? 0.95 : 1.4, primaryCloseBlend),
            xDelta(closeLeadX),
            closeLeadY,
          );
          chasePoint = {
            x: snapToPixel(lerp(chasePoint.x, closePoint.x, primaryCloseBlend * 0.76)),
            y: snapToPixel(lerp(chasePoint.y, closePoint.y, primaryCloseBlend * 0.82)),
          };
        }

        const commitBlend = (role === primaryRole
          ? (negativeRun ? 0.78 : safetyTackleDepth >= 8 ? 0.92 : 0.58)
          : role === cleanupRole
            ? (negativeRun ? 0.56 : 0.42)
            : role === forceRole
              ? 0.26
              : 0.18)
          * (openFieldBlend > 0.1
            ? role === primaryRole
              ? 1 + openFieldBlend * 0.28 * pursuitFocusBoost
              : role === cleanupRole || role === forceRole
                ? 1 + openFieldBlend * 0.18 * pursuitFocusBoost
                : 1 - openFieldBlend * 0.08
            : 1)
          * (rallyStyle === "swarm" ? 1.22 : rallyStyle === "downhill" ? 1.12 : rallyStyle === "vice" ? 0.82 : 0.96)
          * (tackleEnvironment === "crowded" ? 1.06 : tackleEnvironment === "space" ? 0.88 : 1)
          * (role === primaryRole && smoothFinishRun ? lerp(1, 0.9, primaryCloseBlend) : 1);
        desired = {
          x: snapToPixel(lerp(desired.x, chasePoint.x, commitBlend)),
          y: snapToPixel(lerp(desired.y, chasePoint.y, commitBlend)),
        };
      }

      if (frame >= commitStart && !contactState) {
        const closeBand = role === forceRole
          ? (flowKey === "top" ? -1 : flowKey === "bottom" ? 1 : Math.sign(plan.closeOffsetY || 1))
          : Math.sign(plan.closeOffsetY || (roleIndex % 2 === 0 ? -1 : 1));
        desired = curvedApproachTarget(motion, desired, closeBand, {
          nearDistance: role === primaryRole ? 12 : 18,
          farDistance: role === forceRole ? 92 : role === backsideRole ? 84 : 76,
          minOffset: role === primaryRole ? 3 : 5,
          maxOffset: role === forceRole ? 18 : role === cleanupRole ? 14 : 12,
          scale: rallyStyle === "vice" ? 0.86 : rallyStyle === "swarm" ? 1.08 : 1 - openFieldBlend * 0.12,
        });
      }

      if ((extendedMode || openFieldBlend > 0.2) && frame >= commitStart && !heroDefenseRoles.has(role) && !contactState) {
        desired = {
          x: snapToPixel(carrierMotion.x - xDelta(backgroundTrailDistance + Math.abs(plan.closeOffsetX) * 0.14)),
          y: snapToPixel(carrierMotion.y + plan.closeOffsetY * Math.max(runPresentation.backgroundDriftScale ?? 0.54, openFieldBlend * 0.72)),
        };
      }

      if (contactState) {
        const contactPoint = {
          x: snapToPixel(lerp(tackleAnchorPoint.x, carrierMotion.x, 0.24) + xDelta(finishOffsets.primary.x)),
          y: snapToPixel(lerp(tackleAnchorPoint.y, carrierMotion.y, 0.24) + finishOffsets.primary.y),
        };
        const supportPoint = {
          x: snapToPixel(contactPoint.x + xDelta(finishOffsets.support.x - finishOffsets.primary.x)),
          y: snapToPixel(contactPoint.y + (finishOffsets.support.y - finishOffsets.primary.y)),
        };
        const forcePoint = {
          x: snapToPixel(contactPoint.x + xDelta(finishOffsets.force.x - finishOffsets.primary.x)),
          y: snapToPixel(contactPoint.y + (finishOffsets.force.y - finishOffsets.primary.y)),
        };
        if (role === contactState.primaryRole) {
          desired = contactPoint;
        } else if (role === contactState.supportRole) {
          desired = supportPoint;
        } else if (role === forceRole) {
          desired = forcePoint;
        } else {
          desired = {
            x: snapToPixel(lerp(desired.x, contactPoint.x + xDelta(24), 0.18)),
            y: snapToPixel(lerp(desired.y, contactPoint.y, 0.14)),
          };
        }
      }

      const peerPositions = runRoleOrder
        .slice(0, roleIndex)
        .map((peerRole: string) => defenseMotion.get(peerRole))
        .filter(Boolean);
      const spacedDesired = frame >= plan.react
        ? spreadTargetAwayFromPeers(
            desired,
            peerPositions as any[],
            contactState && (role === contactState.primaryRole || role === contactState.supportRole)
              ? 8 * spacingMultiplier
              : roleSpacingDistance(role, "run") * spacingMultiplier,
            contactState ? 0.08 : role === forceRole ? 0.18 : 0.24
          )
        : desired;

      const roleScale = (
        contactState && (role === contactState.primaryRole || role === contactState.supportRole)
          ? (role === contactState.primaryRole ? 0.8 : 0.74) * pursuitMultiplier
          : negativeRun
            ? (role === primaryRole ? 0.82 : role === cleanupRole ? 0.78 : role === forceRole ? 0.68 : 0.62) * pursuitMultiplier
            : (
              role === primaryRole
                ? 0.92 + openFieldBlend * 0.12 * pursuitFocusBoost
                : role === cleanupRole
                  ? 0.86 + openFieldBlend * 0.1 * pursuitFocusBoost
                  : role === forceRole || role === backsideRole
                    ? 0.74 + openFieldBlend * 0.06
                    : (extendedMode || openFieldBlend > 0.2) && !heroDefenseRoles.has(role)
                      ? 0.52 - openFieldBlend * 0.08
                      : 0.66
            ) * pursuitMultiplier
              * (role === primaryRole ? 1 + arrivalJitterFrames * 0.03 : role === cleanupRole ? 1 - arrivalJitterFrames * 0.02 : 1)
      );
      const collisionBurst = !!contactState
        ? role === contactState.primaryRole || role === contactState.supportRole
        : role === primaryRole && pointDistance(motion, carrierMotion) <= finalApproachDistance + 6;
      steerMotionState(motion, spacedDesired, {
        family: "run",
        scale: capRunScale(roleScale, collisionBurst),
        accelRatio: role === primaryRole || role === cleanupRole || role === forceRole ? 0.34 : 0.28,
        inertia: 0.95,
        arriveDistance: contactState ? 12 : 18,
      });

    });

    if (!contactState && terminalReason === "tackle") {
      const primaryMotion = defenseMotion.get(primaryRole);
      const cleanupMotion = defenseMotion.get(cleanupRole);
      const forceMotion = defenseMotion.get(forceRole);
      const primaryGap = primaryMotion ? pointDistance(primaryMotion, carrierMotion) : Number.POSITIVE_INFINITY;
      const cleanupGap = cleanupMotion ? pointDistance(cleanupMotion, carrierMotion) : Number.POSITIVE_INFINITY;
      const forceGap = forceMotion ? pointDistance(forceMotion, carrierMotion) : Number.POSITIVE_INFINITY;
      const contactThreshold = negativeRun
        ? 10
        : Math.max(
            finalApproachDistance,
            safetyTackleDepth >= 8
              ? (tackleEnvironment === "space" ? 24 : 22)
              : tackleEnvironment === "crowded"
                ? 10
                : tackleEnvironment === "space"
                  ? 14
                  : 12
          );
      const minContactFrame = negativeRun
        ? readEnd - 2
        : Math.max(
            handoffFrame + 2,
            safetyTackleDepth >= 8
              ? guideFrames - finisherBufferFrames
              : commitStart + (rallyStyle === "vice" || tackleEnvironment === "space" ? 1 : rallyStyle === "swarm" || rallyStyle === "downhill" ? -2 : -1)
          );
      const terminalThreshold = negativeRun ? 10 : longGain ? 14 : 12;
      const carrierPastTerminal = (carrierMotion.x - terminalPoint.x) * context.direction;
      const readyForFinish = spotReached(carrierMotion, terminalPoint, terminalThreshold)
        || (openFieldMode && carrierPastTerminal >= -12 && Math.abs(carrierMotion.y - terminalPoint.y) <= 18)
        || carrierProgress >= 0.98;
      const smoothFinishReady = smoothFinishRun && (
        carrierProgress >= 0.86
        || pointDistance(carrierMotion, terminalPoint) <= 48
        || (openFieldMode && carrierPastTerminal >= -22 && Math.abs(carrierMotion.y - terminalPoint.y) <= 22)
      );
      const finishingSupportRole = cleanupRole !== primaryRole && cleanupGap <= finalApproachDistance + 10
        ? cleanupRole
        : forceRole !== primaryRole && forceGap <= finalApproachDistance + 12
          ? forceRole
          : supportRole;
      if (
        frame >= Math.max(handoffFrame + 2, minContactFrame - (smoothFinishRun ? 6 : 0)) &&
        primaryGap <= contactThreshold + (smoothFinishRun ? 4 : 0) &&
        (readyForFinish || (smoothFinishReady && primaryGap <= finalApproachDistance + 14))
      ) {
        contactState = {
          startFrame: frame,
          primaryRole,
          supportRole: finishingSupportRole,
          freezeAfter: Math.max(4, (negativeRun ? 5 : gainedYards >= 12 || extendedMode ? 7 : 6) + (smoothFinishRun ? 1 : 0) + (runPresentation.tackleFrameBias ?? 0)),
          primaryThreshold: Math.max(8, finalApproachDistance - (tackleStyle === "gang-tackle" || tackleStyle === "pile-stop" ? 1 : 0)),
          requireSupport: (tackleStyle === "gang-tackle" || tackleStyle === "pile-stop") && !!finishingSupportRole,
        };
      }
    }

    const offenseFrame = playersFromMotionStates(offenseBase, offenseMotion);
    const defenseFrame = playersFromMotionStates(defenseBase, defenseMotion);
    const carrier = findPlayer(offenseFrame, carrierRole) || findPlayer(offenseFrame, "RB") || carrierBase;
    const qb = findPlayer(offenseFrame, "QB") || qbBase;
    const snapBall = centerSnapBallPoint(centerBase, context.direction, context.losX, context.centerY - 6);
    const qbBall = carriedBallPoint(qb, context.losX, context.centerY - 6, context.direction);
    const carrierBall = carriedBallPoint(carrier, context.endX, context.laneY, context.direction);
    let ball = carrierBall;
    if (frame < snapFrameCount) {
      ball = blendBallPoint(snapBall, qbBall, frame / Math.max(1, snapFrameCount));
    } else if (carrierRole !== "QB" && frame < handoffFrame) {
      ball = frame >= handoffBlendStart
        ? blendBallPoint(qbBall, carrierBall, (frame - handoffBlendStart) / Math.max(1, handoffFrame - handoffBlendStart))
        : qbBall;
    } else if (carrierRole === "QB" || frame < handoffFrame) {
      ball = qbBall;
    }

    let shouldFreeze = false;
    const carrierPastTerminal = (carrier.x - terminalPoint.x) * context.direction;
    const carrierTerminalWindow = spotReached(carrier, terminalPoint, negativeRun ? 10 : explosiveRun ? 18 : 12)
      || (openFieldMode && carrierPastTerminal >= -8 && Math.abs(carrier.y - terminalPoint.y) <= 18);
    const touchdownRunoutReady = spotReached(carrier, terminalPoint, 16)
      || ((carrier.x - terminalPoint.x) * context.direction >= -8 && Math.abs(carrier.y - terminalPoint.y) <= 18);
    if (terminalReason === "kneel") {
      shouldFreeze = frame >= handoffFrame + 4 && spotReached(carrier, terminalPoint, 10);
    } else if (terminalReason === "touchdown") {
      shouldFreeze = frame >= handoffFrame + 6
        && ballBreaksPlane(ball.ballX, context.goalLineX, context.direction, 2)
        && touchdownRunoutReady;
    } else if (terminalReason === "out-of-bounds") {
      shouldFreeze = frame >= handoffFrame + 6 && (Math.abs(carrier.y - terminalPoint.y) <= 10 || spotReached(carrier, terminalPoint, 14));
    } else if (contactState) {
      const primary = findPlayer(defenseFrame, contactState.primaryRole);
      const support = findPlayer(defenseFrame, contactState.supportRole);
      const contactFrames = frame - contactState.startFrame;
      const supportReady = !contactState.requireSupport
        || !support
        || pointDistance(support, carrier) <= finalApproachDistance + 8
        || pointDistance(support, primary || carrier) <= finalApproachDistance + 8;
      shouldFreeze = contactFrames >= contactState.freezeAfter
        && !!primary
        && pointDistance(primary, carrier) <= (contactState.primaryThreshold ?? 10)
        && supportReady
        && velocityMagnitude(offenseMotion.get(carrierRole) || { vx: 0, vy: 0 } as any) <= (openFieldMode ? 2.2 : 1.8)
        && carrierTerminalWindow;
    }

    if (!shouldFreeze && frame >= totalFrames - 1 && terminalReason === "tackle") {
      const primary = findPlayer(defenseFrame, primaryRole);
      shouldFreeze = !!primary
        && !!contactState
        && pointDistance(primary, carrier) <= Math.max(10, finalApproachDistance)
        && carrierTerminalWindow;
    }

    const liveFrame = shouldFreeze
      ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
      : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };
    if (shouldFreeze) {
      frozenFrame = liveFrame;
      if (frame >= totalFrames - 1) {
        plannedFrames = frame + 3;
      }
    } else if (frame === plannedFrames - 1) {
      const carrierVelocity = velocityMagnitude(offenseMotion.get(carrierRole) || { vx: 0, vy: 0 } as any);
      const terminalGap = pointDistance(carrier, terminalPoint);
      let shouldExtend = false;
      let extensionScore = terminalGap + carrierVelocity * 10;
      let extensionFrames = terminalReason === "touchdown" ? 4 : 3;

      if (terminalReason === "tackle") {
        const primary = findPlayer(defenseFrame, primaryRole);
        const primaryGap = primary ? pointDistance(primary, carrier) : Number.POSITIVE_INFINITY;
        shouldExtend =
          terminalGap > (negativeRun ? 12 : 18) ||
          primaryGap > Math.max(finalApproachDistance, tackleEnvironment === "space" ? 16 : tackleEnvironment === "crowded" ? 12 : 14) ||
          carrierVelocity > 0.3;
        extensionScore = terminalGap + primaryGap + carrierVelocity * 12;
        extensionFrames = finisherBufferFrames + (longGain ? 2 : 0);
      } else if (terminalReason === "touchdown") {
        const goalGap = Math.max(0, (context.goalLineX - ball.ballX) * context.direction - 2);
        const runoutGap = pointDistance(carrier, terminalPoint);
        shouldExtend = goalGap > 0.5 || runoutGap > 16 || carrierVelocity > 0.24;
        extensionScore = goalGap * 1.8 + runoutGap + carrierVelocity * 10;
        extensionFrames = Math.max(4, Math.min(8, finisherBufferFrames));
      } else if (terminalReason === "out-of-bounds") {
        shouldExtend = terminalGap > 10 || carrierVelocity > 0.24 || Math.abs(carrier.y - terminalPoint.y) > 9;
        extensionScore = terminalGap + Math.abs(carrier.y - terminalPoint.y) * 0.5 + carrierVelocity * 10;
        extensionFrames = Math.max(3, Math.min(6, finisherBufferFrames));
      }

      if (shouldExtend) {
        if (extensionScore < lastExtensionScore - 1.25) {
          lastExtensionScore = extensionScore;
          stalledExtensionWindows = 0;
          plannedFrames += extensionFrames;
        } else if (stalledExtensionWindows < 2) {
          stalledExtensionWindows += 1;
          plannedFrames += Math.max(3, extensionFrames - 1);
        } else {
          plannedFrames = frame + 1;
        }
      }
    }
    frames.push(liveFrame);
  }

  if (!frozenFrame && terminalReason === "tackle" && frames.length) {
    const finishRolesOrdered = Array.from(new Set([primaryRole, cleanupRole, forceRole].filter(Boolean)));
    let carryFrame = cloneLiveFrame(frames[frames.length - 1]);
    let bestFinishGap = Number.POSITIVE_INFINITY;
    let stalledFinishFrames = 0;
    for (let extraFrame = 0; !frozenFrame; extraFrame += 1) {
      const offenseFrame = clonePlayers(carryFrame.offensePlayers);
      const defenseFrame = clonePlayers(carryFrame.defensePlayers);
      const carrier = findPlayer(offenseFrame, carrierRole) || findPlayer(offenseFrame, "RB") || carrierBase;
      const primary = findPlayer(defenseFrame, primaryRole);
      if (!carrier || !primary) {
        break;
      }

      const primaryGap = pointDistance(primary, carrier);
      const runnerTarget = primaryGap <= finalApproachDistance + 8
        ? {
            x: snapToPixel(lerp(carrier.x, terminalPoint.x, 0.28) + xDelta(finishOffsets.carrier.x)),
            y: snapToPixel(lerp(carrier.y, terminalPoint.y, 0.24) + finishOffsets.carrier.y),
          }
        : {
            x: snapToPixel(lerp(carrier.x, throughPoint.x, explosiveRun ? 0.22 : 0.16)),
            y: snapToPixel(lerp(carrier.y, terminalPoint.y, 0.18)),
          };
      const carrierNext = movePointTowards(
        { x: carrier.x, y: carrier.y },
        runnerTarget,
        playerPixelsPerFrame(carrier.speedRating || carrierBase.speedRating || 84, "run", primaryGap <= finalApproachDistance + 8 ? 0.38 : explosiveRun ? 0.54 : 0.48)
      );
      setPlayerState(offenseFrame, carrierRole, { ...carrierNext, moving: true });
      if (carrierRole !== "QB") {
        const qb = findPlayer(offenseFrame, "QB");
        if (qb) {
          setPlayerState(offenseFrame, "QB", {
            x: snapToPixel(lerp(qb.x, orientX(context.losX, -56, context.direction), 0.2)),
            y: snapToPixel(lerp(qb.y, context.centerY - 2, 0.2)),
            moving: true,
          });
        }
      }

      finishRolesOrdered.forEach((role: string, roleIndex: number) => {
        const defender = findPlayer(defenseFrame, role);
        const basePlayer = findPlayer(defenseBase, role) || defender;
        if (!defender || !basePlayer) {
          return;
        }
        const offsets = role === primaryRole
          ? finishOffsets.primary
          : role === supportRole
            ? finishOffsets.support
            : finishOffsets.force;
        let desired = {
          x: snapToPixel(carrierNext.x + xDelta(offsets.x)),
          y: snapToPixel(carrierNext.y + offsets.y),
        };
        const priorPeers = finishRolesOrdered
          .slice(0, roleIndex)
          .map((peerRole: string) => findPlayer(defenseFrame, peerRole))
          .filter(Boolean);
        desired = spreadTargetAwayFromPeers(desired, priorPeers as any[], role === primaryRole ? 4 : 6, 0.06);
        const next = movePointTowards(
          { x: defender.x, y: defender.y },
          desired,
          playerPixelsPerFrame(
            basePlayer.speedRating || 75,
            "run",
            (role === primaryRole ? 0.86 : role === supportRole ? 0.76 : 0.68) * pursuitMultiplier
          )
        );
        setPlayerState(defenseFrame, role, { ...next, moving: true });
      });

      const activeCarrier = findPlayer(offenseFrame, carrierRole) || findPlayer(offenseFrame, "RB") || carrierBase;
      const activePrimary = findPlayer(defenseFrame, primaryRole);
      const activeSupport = supportRole ? findPlayer(defenseFrame, supportRole) : null;
      const activePrimaryGap = activeCarrier && activePrimary ? pointDistance(activePrimary, activeCarrier) : Number.POSITIVE_INFINITY;
      const activeCarrierPastTerminal = activeCarrier ? (activeCarrier.x - terminalPoint.x) * context.direction : Number.NEGATIVE_INFINITY;
      const terminalWindow = !!activeCarrier && (
        spotReached(activeCarrier, terminalPoint, explosiveRun ? 18 : 14)
        || (openFieldMode && activeCarrierPastTerminal >= -8 && Math.abs(activeCarrier.y - terminalPoint.y) <= 18)
      );
      const supportReady = !supportRequired
        || !activeSupport
        || pointDistance(activeSupport, activeCarrier || terminalPoint) <= finalApproachDistance + 8
        || pointDistance(activeSupport, activePrimary || terminalPoint) <= finalApproachDistance + 8;
      const ball = carriedBallPoint(activeCarrier, context.endX, context.laneY, context.direction);
      const closeEnough = !!activeCarrier
        && !!activePrimary
        && activePrimaryGap <= finalApproachDistance
        && terminalWindow
        && supportReady;
      const finishGap = activePrimaryGap + (activeCarrier ? pointDistance(activeCarrier, terminalPoint) * 0.42 : 0);
      if (finishGap < bestFinishGap - 0.75) {
        bestFinishGap = finishGap;
        stalledFinishFrames = 0;
      } else {
        stalledFinishFrames += 1;
      }
      const liveFrame = closeEnough
        ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
        : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };
      if (closeEnough) {
        frozenFrame = liveFrame;
      } else if (stalledFinishFrames >= 18) {
        break;
      }
      frames.push(liveFrame);
      carryFrame = liveFrame;
    }

    if (!frozenFrame) {
      const forcedFrame = cloneLiveFrame(frames[frames.length - 1]);
      const offenseFrame = clonePlayers(forcedFrame.offensePlayers);
      const defenseFrame = clonePlayers(forcedFrame.defensePlayers);
      const carrier = findPlayer(offenseFrame, carrierRole) || findPlayer(offenseFrame, "RB") || carrierBase;
      const primary = findPlayer(defenseFrame, primaryRole);
      if (carrier && primary) {
        setPlayerState(offenseFrame, carrierRole, {
          x: snapToPixel(lerp(carrier.x, terminalPoint.x, 0.16)),
          y: snapToPixel(lerp(carrier.y, terminalPoint.y, 0.18)),
          moving: false,
        });
        setPlayerState(defenseFrame, primaryRole, {
          x: snapToPixel(carrier.x + xDelta(finishOffsets.primary.x)),
          y: snapToPixel(carrier.y + finishOffsets.primary.y),
          moving: true,
        });
        if (supportRequired && supportRole) {
          const support = findPlayer(defenseFrame, supportRole);
          if (support) {
            setPlayerState(defenseFrame, supportRole, {
              x: snapToPixel(carrier.x + xDelta(finishOffsets.support.x)),
              y: snapToPixel(carrier.y + finishOffsets.support.y),
              moving: true,
            });
          }
        }
        const finalCarrier = findPlayer(offenseFrame, carrierRole) || carrier;
        const finalFrame = freezeLiveFrame(offenseFrame, defenseFrame, carriedBallPoint(finalCarrier, context.endX, context.laneY, context.direction));
        frames[frames.length - 1] = finalFrame;
        frozenFrame = finalFrame;
      }
    }
  }

  return frames;
}

function buildRunFumbleLiveSequence(context: any): any[] {
  const returnYards = turnoverReturnYards(context.play);
  const looseYardLine = turnoverSpotYardLine(context.play);
  const looseX = screenXForYard(looseYardLine, context.direction);
  const looseY = context.laneY;
  const defensiveRecovery = isDefensiveFumbleRecovery(context.play);
  const recoveryCallout = defensiveRecovery ? fumbleRecoveryHeadline(context.play) : null;
  const gainedToLoose = looseYardLine - (context.play.startYardLine ?? looseYardLine);
  const preLoosePlay = {
    ...context.play,
    turnover: false,
    defensiveScore: false,
    returnYards: 0,
    returnEndYardLine: undefined,
    endYardLine: looseYardLine,
    visualYards: gainedToLoose,
    yards: gainedToLoose,
    stopReason: 'tackle',
    result: `${context.play.offenseAbbr || context.offense.abbr} presses to the fumble spot.`,
  };
  const preLooseFrames = buildStandardRunLiveSequence({
    ...context,
    play: preLoosePlay,
    endX: looseX,
  });
  const transitionIndex = findFumbleTransitionFrame(preLooseFrames, looseX, looseY);
  const baseFrames = preLooseFrames.slice(0, transitionIndex + 1).map(cloneLiveFrame);
  const seedFrame = baseFrames[baseFrames.length - 1] || cloneLiveFrame(preLooseFrames[preLooseFrames.length - 1]);
  const previousSeedFrame = baseFrames[Math.max(0, baseFrames.length - 2)] || seedFrame;
  const offenseBase = clonePlayers(seedFrame.offensePlayers);
  const defenseBase = clonePlayers(seedFrame.defensePlayers);
  const offenseMotion = motionMapFromFrames(seedFrame.offensePlayers, previousSeedFrame.offensePlayers);
  const defenseMotion = motionMapFromFrames(seedFrame.defensePlayers, previousSeedFrame.defensePlayers);
  const carrierRole = pickRunCarrierRole(seedFrame);
  const recordedRecovererRole = String(context.play?.participants?.recovererRole || '') || null;
  const recoveryRole = recordedRecovererRole && findPlayer(seedFrame.defensePlayers, recordedRecovererRole)
    ? recordedRecovererRole
    : (
      nearestRoleToPoint(
        seedFrame.defensePlayers,
        { x: looseX, y: looseY },
        ['LB1', 'LB2', 'LB3', 'DE1', 'DT1', 'DT2', 'DE2', 'S1', 'S2', 'CB1', 'CB2'],
      ) || 'LB2'
    );
  const supportRoles = nearestRolesToPoint(
    seedFrame.defensePlayers,
    { x: looseX, y: looseY },
    ['DE1', 'DT1', 'DT2', 'DE2', 'LB1', 'LB2', 'LB3', 'S1', 'S2', 'CB1', 'CB2'],
    [recoveryRole],
    returnYards > 0 ? 3 : 4,
  );
  const chaseRoles = nearestRolesToPoint(
    seedFrame.offensePlayers,
    { x: looseX, y: looseY },
    ['RB', 'QB', 'FB', 'TE', 'WR1', 'WR2', 'SLOT', 'LT', 'LG', 'C', 'RG', 'RT'],
    [carrierRole],
    returnYards > 0 ? 4 : 5,
  );
  const offenseHoldRoles = seedFrame.offensePlayers
    .map((player: any) => player.role)
    .filter((role: string) => role !== carrierRole && !chaseRoles.includes(role));
  const defenseTrailRoles = seedFrame.defensePlayers
    .map((player: any) => player.role)
    .filter((role: string) => role !== recoveryRole && !supportRoles.includes(role));
  const returnDirection = Math.sign(context.endX - looseX) || -context.direction;
  const lateralDirection = playHash(context.play, 'fumble-lateral') % 2 === 0 ? -1 : 1;
  const finishTarget = { x: context.endX, y: snapToPixel(looseY + lateralDirection * 2) };
  const carrierSeed = findPlayer(seedFrame.offensePlayers, carrierRole) || seedFrame.offensePlayers[0];
  const looseBallStart = { x: seedFrame.ball.ballX, y: seedFrame.ball.ballY };
  const popPoint = {
    x: snapToPixel(looseBallStart.x + context.direction * 4),
    y: snapToPixel(looseBallStart.y + lateralDirection * 10),
  };
  const bouncePoint = {
    x: snapToPixel(looseX + 6 + returnDirection * (returnYards > 0 ? 10 : 4)),
    y: snapToPixel(looseY + 10 + lateralDirection * 8),
  };
  const settlePoint = {
    x: snapToPixel(looseX + 6 + returnDirection * (returnYards > 0 ? 8 : 0)),
    y: snapToPixel(looseY + 10 + lateralDirection * 4),
  };
  const carrierDownPoint = {
    x: snapToPixel((carrierSeed?.x ?? looseX) - context.direction * 4),
    y: snapToPixel(looseY + lateralDirection * 10),
  };
  const pilePoint = {
    x: snapToPixel(lerp(looseX, settlePoint.x - 6, 0.42)),
    y: snapToPixel(looseY + lateralDirection * 5),
  };
  const recoverySpeed = findPlayer(defenseBase, recoveryRole)?.speedRating || 78;
  const scrambleFrames = returnYards > 0 ? 8 : 10;
  const returnPath = returnYards > 0
    ? compactPathPoints([
        { x: snapToPixel(settlePoint.x - 6), y: snapToPixel(settlePoint.y - 2) },
        {
          x: snapToPixel(lerp(settlePoint.x - 6, context.endX, 0.34)),
          y: snapToPixel(lerp(settlePoint.y - 2, looseY - lateralDirection * 14, 0.4)),
        },
        {
          x: snapToPixel(lerp(settlePoint.x - 6, context.endX, 0.72)),
          y: snapToPixel(lerp(settlePoint.y - 2, looseY + lateralDirection * 8, 0.82)),
        },
        finishTarget,
      ])
    : null;
  const returnDistance = returnPath?.length ? pathDistance(returnPath) : 0;
  const returnFrames = returnPath?.length
    ? clampRange(Math.round(returnDistance / playerPixelsPerFrame(recoverySpeed, 'run', returnYards >= 20 ? 1.04 : 0.98)), 18, 180)
    : 0;
  const finishFrames = returnYards > 0
    ? clampRange(Math.round(pointDistance(settlePoint, finishTarget) / 28), 10, 20)
    : 8;
  const totalExtraFrames = scrambleFrames + returnFrames + finishFrames;
  const tackleRoles = chaseRoles.slice(0, 2);
  const frozenFrames: any[] = [];

  function decorateRecoveryCallout(frame: any, extraFrame: number): any {
    if (!defensiveRecovery || !frame) {
      return frame;
    }
    const takeoverFrames = extraFrame - scrambleFrames;
    if (takeoverFrames < 0) {
      return frame;
    }
    if (recoveryCallout && takeoverFrames < 18) {
      frame.ribbonMessage = recoveryCallout;
    }
    if (takeoverFrames < 12) {
      frame.takeawayFlash = {
        role: recoveryRole,
        teamType: 'defense',
        intensity: clamp01(1 - takeoverFrames / 12),
        color: recoveryCallout?.color,
      };
    }
    return frame;
  }

  for (let extraFrame = 0; extraFrame < totalExtraFrames; extraFrame += 1) {
    if (frozenFrames.length) {
      const heldFrame = decorateRecoveryCallout(cloneLiveFrame(frozenFrames[0]), extraFrame);
      baseFrames.push(heldFrame);
      continue;
    }

    const scrambleAmount = clamp01(extraFrame / Math.max(1, scrambleFrames - 1));
    const inScramble = extraFrame < scrambleFrames;
    const returnFrame = Math.max(0, extraFrame - scrambleFrames);
    const inReturn = returnPath?.length && extraFrame >= scrambleFrames && extraFrame < scrambleFrames + returnFrames;
    const finishAmount = returnPath?.length
      ? clamp01((extraFrame - scrambleFrames - returnFrames) / Math.max(1, finishFrames - 1))
      : clamp01(Math.max(0, extraFrame - (scrambleFrames - 2)) / Math.max(1, finishFrames));
    const recoveryMotion = defenseMotion.get(recoveryRole) || createMotionState(findPlayer(defenseBase, recoveryRole) || defenseBase[0]);
    const carrierMotion = offenseMotion.get(carrierRole) || createMotionState(findPlayer(offenseBase, carrierRole) || offenseBase[0]);

    steerMotionState(carrierMotion, extraFrame < 3 ? carrierDownPoint : carrierDownPoint, {
      family: 'run',
      scale: extraFrame < 2 ? 0.42 : 0.18,
      accelRatio: 0.24,
      inertia: 0.86,
      arriveDistance: 8,
    });

    chaseRoles.forEach((role: string, index: number) => {
      const motion = offenseMotion.get(role);
      if (!motion) {
        return;
      }
      let target = {
        x: snapToPixel(pilePoint.x - returnDirection * (8 + index * 4)),
        y: snapToPixel(pilePoint.y + (index - 1) * 10),
      };

      if (returnPath?.length && extraFrame >= scrambleFrames) {
        const returnTarget = guidePointAt(returnPath, clamp01(returnFrame / Math.max(1, returnFrames)), 'spline');
        target = {
          x: snapToPixel(returnTarget.x - returnDirection * (14 + index * 6)),
          y: snapToPixel(returnTarget.y + (index % 2 === 0 ? -1 : 1) * (8 + index * 4)),
        };
        if (extraFrame >= scrambleFrames + returnFrames - 2) {
          target = {
            x: snapToPixel(returnTarget.x - returnDirection * (index === 0 ? 4 : 10)),
            y: snapToPixel(returnTarget.y + (index === 0 ? 2 : 10)),
          };
        }
      }

      steerMotionState(motion, target, {
        family: 'run',
        scale: returnPath?.length && extraFrame >= scrambleFrames ? (index === 0 ? 0.88 : 0.82) : 0.76,
        accelRatio: index === 0 ? 0.34 : 0.3,
        inertia: 0.92,
        arriveDistance: 12,
      });
    });

    offenseHoldRoles.forEach((role: string, index: number) => {
      const motion = offenseMotion.get(role);
      if (!motion) {
        return;
      }
      const target = returnPath?.length && extraFrame >= scrambleFrames
        ? {
            x: snapToPixel(lerp(looseX, context.endX, 0.28) - returnDirection * (18 + (index % 3) * 8)),
            y: snapToPixel(looseY + ((index % 4) - 1.5) * 18),
          }
        : {
            x: snapToPixel(looseX - returnDirection * (20 + (index % 3) * 10)),
            y: snapToPixel(looseY + ((index % 4) - 1.5) * 18),
          };
      steerMotionState(motion, target, {
        family: 'run',
        scale: 0.58,
        accelRatio: 0.24,
        inertia: 0.9,
        arriveDistance: 16,
      });
    });

    if (inScramble) {
      steerMotionState(recoveryMotion, {
        x: snapToPixel(lerp(looseX - 4, settlePoint.x - 6, scrambleAmount)),
        y: snapToPixel(lerp(looseY, settlePoint.y - 2, scrambleAmount)),
      }, {
        family: 'run',
        scale: 0.82,
        accelRatio: 0.34,
        inertia: 0.92,
        arriveDistance: 10,
      });
    } else if (inReturn && returnPath?.length) {
      const guide = guidePointAt(returnPath, clamp01(returnFrame / Math.max(1, returnFrames)), 'spline');
      steerMotionState(recoveryMotion, guide, {
        family: 'run',
        scale: 0.86,
        accelRatio: 0.34,
        inertia: 0.94,
        arriveDistance: 14,
      });
    } else if (returnPath?.length) {
      const finishGap = pointDistance(recoveryMotion, finishTarget);
      steerMotionState(recoveryMotion, finishTarget, {
        family: 'run',
        scale: finishGap > 44 ? 0.82 : finishGap > 20 ? 0.54 : 0.32,
        accelRatio: 0.28,
        inertia: 0.88,
        arriveDistance: 8,
      });
    } else {
      steerMotionState(recoveryMotion, pilePoint, {
        family: 'run',
        scale: 0.38,
        accelRatio: 0.28,
        inertia: 0.88,
        arriveDistance: 8,
      });
    }

    supportRoles.forEach((role: string, index: number) => {
      const motion = defenseMotion.get(role);
      if (!motion) {
        return;
      }
      let target = {
        x: snapToPixel(pilePoint.x + returnDirection * (4 + index * 4)),
        y: snapToPixel(pilePoint.y + (index % 2 === 0 ? -1 : 1) * (8 + index * 4)),
      };
      if (returnPath?.length && extraFrame >= scrambleFrames) {
        const returnTarget = guidePointAt(returnPath, clamp01(returnFrame / Math.max(1, returnFrames)), 'spline');
        target = index === 0
          ? {
              x: snapToPixel(returnTarget.x - returnDirection * 18),
              y: snapToPixel(returnTarget.y - lateralDirection * 18),
            }
          : {
              x: snapToPixel(returnTarget.x - returnDirection * (8 + index * 6)),
              y: snapToPixel(returnTarget.y + lateralDirection * (10 + index * 4)),
            };
      }
      steerMotionState(motion, target, {
        family: 'run',
        scale: returnPath?.length && extraFrame >= scrambleFrames ? 0.76 : 0.7,
        accelRatio: 0.3,
        inertia: 0.92,
        arriveDistance: 12,
      });
    });

    defenseTrailRoles.forEach((role: string, index: number) => {
      const motion = defenseMotion.get(role);
      if (!motion) {
        return;
      }
      const target = returnPath?.length && extraFrame >= scrambleFrames
        ? {
            x: snapToPixel(lerp(looseX, context.endX, 0.4) - returnDirection * (24 + (index % 3) * 8)),
            y: snapToPixel(looseY + ((index % 4) - 1.5) * 20),
          }
        : {
            x: snapToPixel(looseX - returnDirection * (14 + (index % 3) * 8)),
            y: snapToPixel(looseY + ((index % 4) - 1.5) * 18),
          };
      steerMotionState(motion, target, {
        family: 'run',
        scale: 0.56,
        accelRatio: 0.24,
        inertia: 0.9,
        arriveDistance: 16,
      });
    });

    const offenseFrame = playersFromMotionStates(offenseBase, offenseMotion);
    const defenseFrame = playersFromMotionStates(defenseBase, defenseMotion);
    const carrier = findPlayer(offenseFrame, carrierRole) || offenseFrame[0];
    const recoveryPlayer = findPlayer(defenseFrame, recoveryRole) || defenseFrame[0];
    const ball = buildFumbleBallPoint({
      extraFrame,
      scrambleFrames,
      returnFrames,
      finishFrames,
      looseBallStart,
      popPoint,
      bouncePoint,
      settlePoint,
      pilePoint,
      returnPath,
      recoveryPlayer,
      returnYards,
      returnDirection,
    });

    if (extraFrame >= 2) {
      applyDownState(offenseFrame, carrierRole, true);
    }
    if (!returnPath?.length && extraFrame >= scrambleFrames - 2) {
      applyDownState(defenseFrame, recoveryRole, true);
      supportRoles.slice(0, 2).forEach((role: string) => applyDownState(defenseFrame, role, true));
      chaseRoles.slice(0, 2).forEach((role: string) => applyDownState(offenseFrame, role, true));
    }

    if (returnPath?.length && extraFrame >= scrambleFrames + returnFrames - 1) {
      applyDownState(defenseFrame, recoveryRole, true);
      tackleRoles.forEach((role: string) => applyDownState(offenseFrame, role, true));
    }

    const freezePile = !returnPath?.length
      && extraFrame >= scrambleFrames + finishFrames - 3
      && pointDistance(recoveryPlayer, pilePoint) <= 14;
    const freezeReturn = !!returnPath?.length
      && extraFrame >= scrambleFrames + returnFrames + Math.max(2, finishFrames - 4)
      && pointDistance(recoveryPlayer, finishTarget) <= 14
      && tackleRoles.every((role: string) => {
        const tackler = findPlayer(offenseFrame, role);
        return !tackler || pointDistance(tackler, recoveryPlayer) <= 18;
      });

    const liveFrame = (freezePile || freezeReturn)
      ? freezeLiveFrame(offenseFrame, defenseFrame, {
          ballX: ball.ballX,
          ballY: ball.ballY,
        })
      : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };

    decorateRecoveryCallout(liveFrame, extraFrame);

    if (freezePile || freezeReturn) {
      frozenFrames.push(liveFrame);
    }

    baseFrames.push(liveFrame);
  }

  if (returnPath?.length && !frozenFrames.length) {
    const maxExtensionFrames = clampRange(
      Math.round(returnDistance / playerPixelsPerFrame(recoverySpeed, 'run', 1.02)),
      18,
      160
    );
    for (let extensionFrame = 0; extensionFrame < maxExtensionFrames; extensionFrame += 1) {
      const recoveryMotion = defenseMotion.get(recoveryRole) || createMotionState(findPlayer(defenseBase, recoveryRole) || defenseBase[0]);
      const carrierMotion = offenseMotion.get(carrierRole) || createMotionState(findPlayer(offenseBase, carrierRole) || offenseBase[0]);
      const recoveryGap = pointDistance(recoveryMotion, finishTarget);
      steerMotionState(recoveryMotion, finishTarget, {
        family: 'run',
        scale: recoveryGap > 44 ? 0.84 : recoveryGap > 20 ? 0.58 : 0.34,
        accelRatio: 0.28,
        inertia: 0.88,
        arriveDistance: 8,
      });
      steerMotionState(carrierMotion, carrierDownPoint, {
        family: 'run',
        scale: 0.1,
        accelRatio: 0.2,
        inertia: 0.82,
        arriveDistance: 6,
      });

      tackleRoles.forEach((role: string, index: number) => {
        const motion = offenseMotion.get(role);
        if (!motion) {
          return;
        }
        steerMotionState(motion, {
          x: snapToPixel(finishTarget.x - returnDirection * (index === 0 ? 4 : 10)),
          y: snapToPixel(finishTarget.y + (index === 0 ? 2 : 10)),
        }, {
          family: 'run',
          scale: index === 0 ? 0.84 : 0.76,
          accelRatio: 0.32,
          inertia: 0.9,
          arriveDistance: 10,
        });
      });

      supportRoles.forEach((role: string, index: number) => {
        const motion = defenseMotion.get(role);
        if (!motion) {
          return;
        }
        steerMotionState(motion, {
          x: snapToPixel(finishTarget.x - returnDirection * (16 + index * 6)),
          y: snapToPixel(finishTarget.y + (index % 2 === 0 ? -1 : 1) * (12 + index * 4)),
        }, {
          family: 'run',
          scale: 0.66,
          accelRatio: 0.28,
          inertia: 0.9,
          arriveDistance: 12,
        });
      });

      offenseHoldRoles.forEach((role: string, index: number) => {
        const motion = offenseMotion.get(role);
        if (!motion) {
          return;
        }
        steerMotionState(motion, {
          x: snapToPixel(finishTarget.x - returnDirection * (22 + (index % 3) * 8)),
          y: snapToPixel(finishTarget.y + ((index % 4) - 1.5) * 18),
        }, {
          family: 'run',
          scale: 0.5,
          accelRatio: 0.22,
          inertia: 0.88,
          arriveDistance: 16,
        });
      });

      defenseTrailRoles.forEach((role: string, index: number) => {
        const motion = defenseMotion.get(role);
        if (!motion) {
          return;
        }
        steerMotionState(motion, {
          x: snapToPixel(finishTarget.x - returnDirection * (26 + (index % 3) * 8)),
          y: snapToPixel(finishTarget.y + ((index % 4) - 1.5) * 20),
        }, {
          family: 'run',
          scale: 0.48,
          accelRatio: 0.22,
          inertia: 0.88,
          arriveDistance: 18,
        });
      });

      const offenseFrame = playersFromMotionStates(offenseBase, offenseMotion);
      const defenseFrame = playersFromMotionStates(defenseBase, defenseMotion);
      const recoveryPlayer = findPlayer(defenseFrame, recoveryRole) || defenseFrame[0];
      const ball = carriedBallPoint(recoveryPlayer, settlePoint.x, settlePoint.y, returnDirection);

      applyDownState(offenseFrame, carrierRole, true);
      if (extensionFrame >= 3) {
        applyDownState(defenseFrame, recoveryRole, true);
        tackleRoles.forEach((role: string) => applyDownState(offenseFrame, role, true));
      }

      const shouldFreeze = pointDistance(recoveryPlayer, finishTarget) <= 12
        && tackleRoles.every((role: string) => {
          const tackler = findPlayer(offenseFrame, role);
          return !tackler || pointDistance(tackler, recoveryPlayer) <= 18;
        });
      const liveFrame = shouldFreeze
        ? freezeLiveFrame(offenseFrame, defenseFrame, ball)
        : { offensePlayers: offenseFrame, defensePlayers: defenseFrame, ball };

      decorateRecoveryCallout(liveFrame, scrambleFrames + returnFrames + finishFrames + extensionFrame);
      baseFrames.push(liveFrame);
      if (shouldFreeze) {
        break;
      }
    }
  }

  return baseFrames;
}

function pickRunCarrierRole(frame: any): string {
  const candidates = ['RB', 'FB', 'QB', 'TE', 'WR1', 'WR2', 'SLOT'];
  const ballPoint = { x: frame.ball.ballX - 6, y: frame.ball.ballY - 8 };
  return nearestRoleToPoint(frame.offensePlayers, ballPoint, candidates) || 'RB';
}

function motionMapFromFrames(players: FieldPlayer[], previousPlayers: FieldPlayer[]): Map<string, any> {
  return new Map(players.map((player: FieldPlayer) => {
    const state = createMotionState(player);
    const previous = findPlayer(previousPlayers, player.role);
    if (previous) {
      state.vx = player.x - previous.x;
      state.vy = player.y - previous.y;
    }
    return [player.role, state];
  }));
}

function findFumbleTransitionFrame(frames: any[], targetX: number, targetY: number): number {
  for (let index = 0; index < frames.length; index += 1) {
    const frame = frames[index];
    const carrier = carrierInRunFrame(frame);
    if (carrier && spotReached(carrier, { x: targetX, y: targetY }, 12)) {
      return Math.max(0, index);
    }
  }
  return Math.max(0, frames.length - 5);
}

function carrierInRunFrame(frame: any): any {
  return findPlayer(frame.offensePlayers, 'RB')
    || findPlayer(frame.offensePlayers, 'FB')
    || findPlayer(frame.offensePlayers, 'QB')
    || frame.offensePlayers[0];
}

function nearestRoleToPoint(players: any[], target: { x: number; y: number }, allowedRoles: string[]): string | null {
  const nearest = nearestPlayerToPoint(players, target, allowedRoles);
  return nearest?.role || null;
}

function nearestRolesToPoint(players: any[], target: { x: number; y: number }, allowedRoles: string[], excludeRoles: string[] = [], count: number = 1): string[] {
  return players
    .filter((player: any) => allowedRoles.includes(player.role) && !excludeRoles.includes(player.role))
    .map((player: any) => ({ role: player.role, distance: pointDistance(player, target) }))
    .sort((left: any, right: any) => left.distance - right.distance)
    .slice(0, count)
    .map((entry: any) => entry.role);
}

function applyDownState(players: any[], role: string, tackled: boolean = true): void {
  const player = findPlayer(players, role);
  if (!player) {
    return;
  }
  player.down = true;
  player.tackled = tackled;
  player.moving = false;
}

function buildFumbleBallPoint(options: any): { ballX: number; ballY: number } {
  const {
    extraFrame,
    scrambleFrames,
    returnFrames,
    finishFrames,
    looseBallStart,
    popPoint,
    bouncePoint,
    settlePoint,
    pilePoint,
    returnPath,
    recoveryPlayer,
    returnYards,
    returnDirection,
  } = options;

  if (extraFrame < 2) {
    const amount = clamp01(extraFrame / 1);
    return {
      ballX: snapToPixel(lerp(looseBallStart.x, popPoint.x, amount)),
      ballY: snapToPixel(lerp(looseBallStart.y, popPoint.y, amount) - Math.sin(amount * Math.PI) * 12),
    };
  }

  if (extraFrame < scrambleFrames) {
    const amount = clamp01((extraFrame - 2) / Math.max(1, scrambleFrames - 3));
    return {
      ballX: snapToPixel(lerp(popPoint.x, bouncePoint.x, amount)),
      ballY: snapToPixel(lerp(popPoint.y, bouncePoint.y, amount) - Math.sin(amount * Math.PI) * (returnYards > 0 ? 10 : 8)),
    };
  }

  if (!returnPath?.length) {
    return {
      ballX: snapToPixel(lerp(settlePoint.x, pilePoint.x + 6, clamp01((extraFrame - scrambleFrames) / Math.max(1, finishFrames)))),
      ballY: snapToPixel(lerp(settlePoint.y, pilePoint.y + 8, clamp01((extraFrame - scrambleFrames) / Math.max(1, finishFrames)))),
    };
  }

  if (extraFrame < scrambleFrames + returnFrames) {
    return carriedBallPoint(recoveryPlayer, settlePoint.x, settlePoint.y, returnDirection);
  }

  return carriedBallPoint(recoveryPlayer, settlePoint.x, settlePoint.y, returnDirection);
}
